<template>
    <div v-cloak>
        <div class="row">
            <div class="col-md-12">
                <nav aria-label="breadcrumb">
                    <ol class="breadcrumb">
                        <li class="breadcrumb-item">
                            <router-link :to="{ name: 'home' }">{{ $t("inicio") }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="{ name: 'integraciones' }">{{ $t("integraciones") }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="{ name: 'businesscentral' }">{{ $t("businessCentral") }}</router-link>
                        </li>
                        <li class="breadcrumb-item active" aria-current="page">{{ $t("exportacionVentasBC") }}</li>
                    </ol>
                </nav>
            </div>

            <!-- Seleccion de que se exporta y filtros de pago -->
            <CabeceraBusinessCentral ref="cabeceraBC" :arbol="arbolExportacion" :seleccion="seleccion"
                :cambiosPendientes="hayCambiosSinCargar" :cargando="cargandoVentas"
                @seleccionChanged="onSeleccionChanged" @aplicar="aplicarSeleccion" @informes="abrirInformes" />

            <!-- Deshabilitar el modal de exportación a BC -->
            <div v-if="showFloatingBtn" :class="['floating-btn-container', { 'floating-btn-raised': isContentShort }]">
                <button class="floating-btn" :disabled="exportando" @click="showExportDataWarning()">
                    <i class="fa" :class="exportando ? 'fa-spinner fa-spin' : 'fa-upload'"></i>
                    {{ exportando ? $t("evExportando") : $t("exportData") }}
                </button>
            </div>

            <template v-if="(cargandoVentas || !consultaResuelta) && daysToExport.length === 0">
                <div class="col-md-12">
                    <div class="card ev-cargando">
                        <i class="fa fa-spinner fa-spin"></i> {{ $t("evCargandoVentas") }}
                    </div>
                </div>
            </template>

            <!-- En caso de error en consulta -->
            <template v-else-if="errorCargaVentas && daysToExport.length === 0">
                <div class="col-md-12">
                    <div class="alert alert-danger text-center">
                        <h3><i class="fas fa-exclamation-circle"></i> {{ $t("evErrorCargaVentas") }}.</h3>
                        <p>{{ $t("evErrorCargaVentasTexto") }}</p>

                        <button class="btn-refrescar-icono" :disabled="refrescando"
                            :title="$t('refrescar')" :aria-label="$t('refrescar')" @click="refrescarDatos()">
                            <i class="fa fa-sync-alt" :class="{ 'fa-spin': refrescando }"></i>
                        </button>
                    </div>
                </div>
            </template>

            <!-- No hay datos -->
            <template v-else-if="daysToExport.length === 0">
                <div class="col-md-12">
                    <div class="alert alert-success text-center">
                        <h3><i class="fas fa-check-circle"></i> {{ $t("noHayDiasParaExportar") }}.</h3>
                        <p>{{ $t("todoExportadoCorrectamente") }}</p>

                        <!-- Refrescar sin salir y volver a entrar. Solo en modo desarrollador. -->
                        <button v-if="developerMode" class="btn-refrescar-icono" :disabled="refrescando"
                            :title="$t('refrescar')" :aria-label="$t('refrescar')" @click="refrescarDatos()">
                            <i class="fa fa-sync-alt" :class="{ 'fa-spin': refrescando }"></i>
                        </button>
                    </div>
                </div>
            </template>

            <!-- No hay datos para esta forma de pago -->
            <template v-else-if="filteredDaysToExport.length === 0">
                <div class="col-md-12">
                    <div class="alert alert-success text-center">
                        <h3><i class="fas fa-check-circle"></i> {{ $t("noHayVentasConEstaFormaPago") }}.</h3>
                        <p>{{ $t("formaPagoNoEncontrada") }}</p>
                    </div>
                </div>
            </template>

            <!-- Hay datos -->
            <template v-else>
                <div class="col-md-12">

                    <!-- Resumen de avisos: una linea; al desplegar, agrupados por tipo.
                         Pulsar un grupo filtra la tabla por ese aviso. -->
                    <div class="ev-resumen-avisos" :class="resumenAvisosClase">
                        <div class="ev-resumen-avisos-cabecera" @click="avisosDesplegados = !avisosDesplegados">
                            <i class="fas"
                                :class="salesWithWarnings.length ? 'fa-exclamation-triangle' : 'fa-check-circle'"></i>
                            <span class="ev-resumen-avisos-texto">
                                <strong>{{ filteredDaysToExport.length }}</strong> {{ $t("evDocumentosPendientes") }}
                                <template v-if="exportDate"> {{ $t("evDelDiaDeCaja") }} <strong>{{ exportDate
                                        }}</strong></template>
                                · <strong>{{ formatAmount(totalFiltrado) }}</strong>
                                <template v-if="salesWithWarnings.length">
                                    · <strong>{{ salesWithWarnings.length }}</strong> {{ $t("evConAvisos") }}
                                    <template v-if="ventasBloqueantes.length">
                                        (<strong>{{ ventasBloqueantes.length }}</strong> {{ $t("evNoSeExportaran")
                                        }})
                                    </template>
                                </template>
                                <template v-else> · {{ $t("evSinAvisos") }}</template>
                                <template v-if="ventasInformativas.length"> · <strong>{{ ventasInformativas.length
                                        }}</strong> {{ $t("evConNotasInformativas") }}</template>
                                <template v-if="cuentasSinCobrar.documentos"> · <strong>{{ cuentasSinCobrar.documentos
                                        }}</strong> {{ $t("evCuentasSinCobrar") }}</template>
                            </span>
                            <i v-if="cargandoVentas" class="fa fa-spinner fa-spin"></i>
                            <span v-if="avisosPorTipo.length || cuentasSinCobrar.documentos" class="ev-resumen-avisos-accion">
                                {{ avisosDesplegados ? $t("evOcultarDetalles") : $t("evVerDetalles") }}
                                <i class="fas" :class="avisosDesplegados ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                            </span>
                        </div>
                        <div v-if="avisosDesplegados && (avisosPorTipo.length || cuentasSinCobrar.documentos)"
                            class="ev-resumen-avisos-cuerpo">
                            <small v-if="avisosPorTipo.length" class="ev-ayuda">{{ $t("evFiltrarPorAviso") }}</small>
                            <div v-for="grupo in avisosPorTipo" :key="'g-' + grupo.codigo" class="ev-aviso-grupo"
                                :class="['ev-aviso-' + grupo.nivel, { 'ev-aviso-grupo-activo': filtroAviso === grupo.codigo }]"
                                @click="toggleFiltroAviso(grupo.codigo)">
                                <div class="ev-aviso-grupo-titulo">
                                    <i class="fas" :class="nivelIcono(grupo.nivel)"></i>
                                    {{ $t(grupo.codigo) }}
                                    <span class="ev-contador">{{ grupo.ventas }}</span>
                                    <span class="ev-nivel-etiqueta">{{ nivelLabel(grupo.nivel) }}</span>
                                    <i v-if="filtroAviso === grupo.codigo" class="fas fa-filter ev-aviso-filtrado"></i>
                                </div>
                                <ul v-if="grupo.detalles.length">
                                    <li v-for="d in grupo.detalles.slice(0, 8)" :key="'gd-' + grupo.codigo + '-' + d.texto">
                                        {{ d.texto }} <span class="text-muted">({{ d.veces }})</span>
                                    </li>
                                    <li v-if="grupo.detalles.length > 8" class="text-muted">
                                        {{ $t("evYOtros", { n: grupo.detalles.length - 8 }) }}
                                    </li>
                                </ul>
                            </div>

                            <!-- Cuentas sin cobrar del dia: no estan en la tabla porque no se
                                 exportan, asi que no filtran; solo informan -->
                            <div v-if="cuentasSinCobrar.documentos" class="ev-aviso-grupo ev-aviso-info ev-aviso-grupo-fijo">
                                <div class="ev-aviso-grupo-titulo">
                                    <i class="fas fa-hourglass-half"></i>
                                    {{ $t("evCuentasSinCobrarTitulo") }}
                                    <span class="ev-contador">{{ cuentasSinCobrar.documentos }}</span>
                                    <span class="ev-nivel-etiqueta">{{ $t("evNivelInfo") }}</span>
                                </div>
                                <ul>
                                    <li>{{ $t("evCuentasSinCobrarTexto", { importe: formatAmount(cuentasSinCobrar.importe) }) }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <!-- Resumen previo a la exportación: lo mismo que va a ver
                         Business Central, agrupado antes de mandarlo. Va en tabla
                         para que nombre, código, documentos e importe queden en
                         columna aunque los textos tengan largos distintos. -->
                    <div class="resumen-exportacion">
                        <div class="resumen-bloque">
                            <!-- Como contar las ventas con varios pagos (avanzadas); afecta a la tarjeta y a la tabla -->
                            <h6 class="resumen-titulo-con-selector">
                                <span><i class="fas fa-coins"></i> {{ $t("resumenPorMedioPago") }}</span>
                                <select class="form-control ev-select ev-select-avanzadas" v-model="modoAvanzadas"
                                    :title="$t('evModoAvanzadasTitulo')">
                                    <option value="todas">{{ $t("evAvanzadasTodas") }}</option>
                                    <option value="solo">{{ $t("evAvanzadasSolo") }}</option>
                                    <option value="sin">{{ $t("evAvanzadasSin") }}</option>
                                </select>
                            </h6>
                            <table class="tabla-resumen">
                                <tbody>
                                    <tr v-for="fila in summaryByPaymentMethod" :key="'medio-' + fila.clave"
                                        class="resumen-fila-filtro"
                                        :class="{ 'resumen-fila-activa': filtroMedioResumen === fila.clave }"
                                        @click="toggleFiltro('filtroMedioResumen', fila.clave)">
                                        <td class="resumen-nombre">{{ fila.nombre }}</td>
                                        <td class="resumen-codigo">
                                            <code v-if="fila.codigoBC" class="chip-bc">{{ fila.codigoBC }}</code>
                                        </td>
                                        <td class="resumen-docs"><span class="contador">{{ fila.documentos }}</span>
                                        </td>
                                        <td class="resumen-importe">{{ formatAmount(fila.importe) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="resumen-bloque">
                            <h6><i class="fas fa-cash-register"></i> {{ $t("resumenPorTpv") }}</h6>
                            <table class="tabla-resumen">
                                <tbody>
                                    <tr v-for="fila in summaryByTpv" :key="'tpv-' + fila.clave"
                                        class="resumen-fila-filtro"
                                        :class="{ 'resumen-fila-activa': filtroTpv === fila.clave }"
                                        @click="toggleFiltro('filtroTpv', fila.clave)">
                                        <td class="resumen-nombre">{{ fila.nombre }}</td>
                                        <td class="resumen-docs"><span class="contador">{{ fila.documentos }}</span>
                                        </td>
                                        <td class="resumen-importe">{{ formatAmount(fila.importe) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="resumen-bloque">
                            <h6><i class="fas fa-file-invoice"></i> {{ $t("resumenPorTipoDocumento") }}</h6>
                            <table class="tabla-resumen">
                                <tbody>
                                    <tr v-for="fila in summaryByDocumentType" :key="'doc-' + fila.clave"
                                        class="resumen-fila-filtro"
                                        :class="{ 'resumen-fila-activa': filtroTipoDoc === fila.clave }"
                                        @click="toggleFiltro('filtroTipoDoc', fila.clave)">
                                        <td class="resumen-nombre">{{ fila.nombre }}</td>
                                        <td class="resumen-docs"><span class="contador">{{ fila.documentos }}</span>
                                        </td>
                                        <td class="resumen-importe">{{ formatAmount(fila.importe) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Barra de la tabla: filtros locales y paginacion -->
                    <div ref="barraFiltros" class="ev-barra">
                        <div class="ev-buscador">
                            <i class="fas fa-search"></i>
                            <input type="text" class="form-control" v-model="busqueda"
                                :placeholder="$t('evBuscarVenta')" />
                            <button v-if="busqueda" type="button" class="ev-limpiar" :title="$t('limpiar')"
                                @click="busqueda = ''">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>

                        <div class="ev-chips">
                            <button v-for="chip in chipsVista" :key="'chip-' + chip.valor" type="button"
                                class="ev-chip" :class="['ev-chip-' + chip.valor, { 'ev-chip-activo': vista === chip.valor }]"
                                @click="vista = chip.valor">
                                {{ chip.etiqueta }} <span class="ev-chip-num">{{ chip.num }}</span>
                            </button>
                        </div>

                        <!-- Desplegables pegados a la derecha. "Quitar filtros" va a su
                             izquierda en pantallas anchas y a su derecha en estrechas -->
                        <div class="ev-barra-derecha">
                            <select v-if="summaryByTpv.length > 1" class="form-control ev-select" v-model="filtroTpv">
                                <option value="">{{ $t("evTodosLosTpv") }}</option>
                                <option v-for="fila in summaryByTpv" :key="'otpv-' + fila.clave" :value="fila.clave">{{
                                    fila.nombre }}</option>
                            </select>

                            <select class="form-control ev-select ev-select-tipo-doc" v-model="filtroTipoDoc">
                                <option value="">{{ $t("todosLosDocumentos") }}</option>
                                <option v-for="fila in summaryByDocumentType" :key="'odoc-' + fila.clave"
                                    :value="fila.clave">{{ fila.nombre }}</option>
                            </select>

                            <button v-if="hayFiltrosLocales" type="button" class="ev-btn-quitar"
                                @click="quitarFiltrosLocales()">
                                <i class="fas fa-filter"></i> {{ $t("evQuitarFiltros") }}
                            </button>
                        </div>
                    </div>

                    <div v-if="filtroAviso || filtroMedioResumen || filtroTpv || filtroTipoDoc" class="ev-filtros-activos">
                        <span v-if="filtroTpv" class="ev-filtro-activo">
                            {{ $t("tpv") }}: {{ nombreFiltroTpv }}
                            <i class="fas fa-times" @click="filtroTpv = ''"></i>
                        </span>
                        <span v-if="filtroTipoDoc" class="ev-filtro-activo">
                            {{ $t("tipoDocumento") }}: {{ nombreFiltroTipoDoc }}
                            <i class="fas fa-times" @click="filtroTipoDoc = ''"></i>
                        </span>
                        <span v-if="filtroAviso" class="ev-filtro-activo">
                            {{ $t("evFiltroAviso") }}: {{ $t(filtroAviso) }}
                            <i class="fas fa-times" @click="filtroAviso = ''"></i>
                        </span>
                        <span v-if="filtroMedioResumen" class="ev-filtro-activo">
                            {{ $t("medioDePago") }}: {{ nombreMedioResumen }}
                            <i class="fas fa-times" @click="filtroMedioResumen = ''"></i>
                        </span>
                    </div>

                    <div class="table-responsive">
                        <table class="table table-bordered table-hover tabla-exportacion">
                            <thead class="thead-dark">
                                <tr>
                                    <th style="width: 34px;"></th>
                                    <!-- Clic en la cabecera: ascendente, descendente y vuelta al orden original -->
                                    <th v-for="col in columnasOrdenables" :key="'th-' + col.campo"
                                        class="th-ordenable" :class="[col.clase, { 'th-ordenada': orden.campo === col.campo }]"
                                        @click="ordenarPor(col.campo)">
                                        {{ $t(col.etiqueta) }}
                                        <i class="fas th-orden-icono" :class="iconoOrden(col.campo)"></i>
                                    </th>
                                    <th class="text-center">{{ $t("acciones") }}</th>
                                </tr>
                            </thead>

                            <!-- Un tbody por venta: así la fila de detalle viaja con la
                                 suya y la clave va en un elemento real, no en un
                                 <template>, que Vue 2 no admite keyed. -->
                            <template v-if="ventasVista.length > 0">
                                <tbody v-for="venta in ventasPagina" :key="'venta-' + venta.id"
                                    :id="'ev-venta-' + venta.id" class="grupo-venta"
                                    :class="{
                                        'grupo-venta-resaltada': resaltadoId === venta.id,
                                        'grupo-venta-abierta': isRowOpen(venta),
                                        ['grupo-nivel-' + nivelVenta(venta)]: isRowOpen(venta) && nivelVenta(venta),
                                    }">
                                    <tr :class="filaClase(venta)">
                                        <td class="text-center col-desplegar">
                                            <button v-if="hasDetail(venta)" type="button" class="btn-desplegar"
                                                :aria-label="$t('verDetalle')" @click="toggleDetail(venta)">
                                                <i class="fas"
                                                    :class="isRowOpen(venta) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                            </button>
                                            <span v-if="hasAvisos(venta)" class="ev-icono-aviso"
                                                :class="'ev-icono-' + nivelVenta(venta)"
                                                v-tooltip.right="tooltipAvisos(venta)" @click="toggleDetail(venta)">
                                                <i class="fas" :class="nivelIcono(nivelVenta(venta))"></i>
                                                <span v-if="venta.avisos.length > 1" class="ev-icono-num">{{
                                                    venta.avisos.length }}</span>
                                            </span>
                                        </td>

                                        <td>
                                            {{ venta.fechaDocumento || venta.format_date }}
                                            <small v-if="venta.horaDocumento" class="d-block text-muted">{{
                                                venta.horaDocumento }}</small>
                                        </td>

                                        <td>
                                            {{ venta.tpvName || '-' }}
                                            <small class="d-block text-muted">{{ venta.workerName || '-' }}</small>
                                        </td>

                                        <td>
                                            <div class="doc-linea">
                                                <span class="badge-doc"
                                                    :class="'badge-doc-' + (venta.tipoDocumento || 'T')">
                                                    {{ documentTypeLabel(venta.tipoDocumento) }}
                                                </span>
                                                <span class="doc-numero">{{ venta.docNumber || venta.ref_docNumber ||
                                                    '-' }}</span>
                                                <span v-if="venta.esFacturaQR" class="chip-origen"
                                                    v-tooltip.top-center="$i18n.t('facturaQR')">QR</span>
                                                <!-- Estado de exportacion distinto de pendiente -->
                                                <span v-if="estadoLabel(venta)" class="chip-estado"
                                                    :class="'chip-estado-' + venta.estadoExportacion">{{ estadoLabel(venta) }}</span>
                                            </div>
                                            <div v-if="venta.esRectificativa" class="doc-rectifica">
                                                <i class="fas fa-reply"></i>
                                                <span>{{ $t("rectificaA") }}</span>
                                                <!-- Enlace con salto al original de la misma exportacion pendiente -->
                                                <a v-if="originalEnLista(venta)" class="doc-numero doc-enlace"
                                                    v-tooltip.top-center="$i18n.t('evIrAlDocumento')"
                                                    @click="irADocumento(originalEnLista(venta))">
                                                    {{ venta.documentoRectificado }}
                                                </a>
                                                <span v-else class="doc-numero">{{ venta.documentoRectificado || '-'
                                                    }}</span>
                                                <span v-if="venta.documentoRectificadoExportado"
                                                    class="chip-exportado"
                                                    v-tooltip.top-center="$i18n.t('evOriginalYaExportadoTexto', { fecha: venta.fechaDocumentoRectificado || '-' })">
                                                    {{ $t("evOriginalYaExportado") }}
                                                </span>
                                                <i v-if="!venta.documentoRectificadoLocalizado"
                                                    class="fas fa-exclamation-triangle"
                                                    v-tooltip.top-center="$i18n.t('avisoRectificativaSinOriginal')"></i>
                                            </div>
                                            <!-- Enlace inverso: el original apunta a sus rectificativas -->
                                            <div v-if="rectificativasDe(venta).length" class="doc-rectificada">
                                                <i class="fas fa-share"></i>
                                                <span>{{ $t("evRectificadaPor") }}</span>
                                                <a v-for="r in rectificativasDe(venta)" :key="'rp-' + venta.id + '-' + r.id"
                                                    class="doc-numero doc-enlace"
                                                    v-tooltip.top-center="$i18n.t('evIrAlDocumento')"
                                                    @click="irADocumento(r)">{{ r.docNumber }}</a>
                                            </div>
                                        </td>

                                        <td class="col-cliente">
                                            <template v-if="venta.cliente">
                                                <span>{{ venta.cliente.nombreCompleto || '-' }}</span>
                                                <small class="d-block text-muted">
                                                    <span v-if="venta.cliente.documento">{{ venta.cliente.documento
                                                        }}</span>
                                                    <span v-if="venta.cliente.esSocio" class="badge-socio">
                                                        {{ $t("socio") }} {{ venta.cliente.codigo || '?' }}
                                                    </span>
                                                </small>
                                            </template>
                                            <span v-else>-</span>
                                        </td>

                                        <td>{{ venta.formaPago || venta.tipoFormaPago || '-' }}</td>

                                        <td>
                                            <template v-if="venta.paidType === 'A'">
                                                <span class="texto-varias">{{ $t("variasFormas") }}</span>
                                                <small class="d-block text-muted">{{ paymentBreakdownSummary(venta)
                                                    }}</small>
                                            </template>
                                            <template v-else>
                                                {{ venta.medioPago || '-' }}
                                                <code v-if="venta.codigoBC" class="chip-bc">{{ venta.codigoBC }}</code>
                                            </template>
                                        </td>

                                        <td class="text-right">
                                            <strong>{{ formatAmount(venta.importe) }}</strong>
                                            <small v-if="amountSourceLabel(venta)" class="d-block text-muted">
                                                {{ amountSourceLabel(venta) }}
                                            </small>
                                        </td>

                                        <td class="text-center col-comensales">{{ venta.num_comensales }}
                                        </td>

                                        <td class="acciones-col text-center" style="width: 100px;">
                                            <!-- Mientras se excluye o incluye esta venta, sus botones se bloquean -->
                                            <div class="acciones-btns" :class="{ 'acciones-ocupadas': cambiandoExclusion[venta.id] }">
                                                <a class="accion-icono accion-info"
                                                    @click="verDetallesFormaPago(venta)">
                                                    <i v-tooltip.top-center="$i18n.t('verDetallesVenta')"
                                                        class="fas fa-info-circle"></i>
                                                </a>
                                                <a v-if="permisoEdicionBC && venta.editable"
                                                    class="accion-icono accion-editar" @click="editarFormaPago(venta)">
                                                    <i v-tooltip.top-center="$i18n.t('editarMedioPago')"
                                                        class="fas fa-pencil-alt"></i>
                                                </a>
                                                <span v-else-if="permisoEdicionBC"
                                                    class="accion-icono accion-bloqueada">
                                                    <i v-tooltip.top-center="notEditableReason(venta)"
                                                        class="fas fa-lock"></i>
                                                </span>
                                                <!-- Excluir de la exportacion (estado 4) o volver a incluir -->
                                                <a class="accion-icono"
                                                    :class="venta.estadoExportacion === 4 ? 'accion-incluir' : 'accion-excluir'"
                                                    @click="toggleExclusion(venta)">
                                                    <i v-tooltip.top-center="venta.estadoExportacion === 4
                                                        ? $i18n.t('evIncluirEnExportacion') : $i18n.t('evExcluirDeExportacion')"
                                                        class="fas" :class="cambiandoExclusion[venta.id] ? 'fa-spinner fa-spin'
                                                            : (venta.estadoExportacion === 4 ? 'fa-undo' : 'fa-ban')"></i>
                                                </a>
                                            </div>
                                        </td>
                                    </tr>

                                    <!-- Detalle: desglose de pagos, vales y avisos -->
                                    <!-- La tarjeta deja libre la primera y la ultima columna -->
                                    <tr v-if="isRowOpen(venta)" class="fila-detalle">
                                        <td></td>
                                        <td colspan="8">
                                            <div class="detalle-panel">
                                                <!-- Avisos primero: es lo que hay que resolver -->
                                                <div v-if="hasAvisos(venta)" class="detalle-bloque detalle-avisos">
                                                    <h6>{{ $t("avisos") }}</h6>
                                                    <ul class="lista-avisos">
                                                        <li v-for="(aviso, i) in venta.avisos"
                                                            :key="'aviso-' + venta.id + '-' + i"
                                                            :class="'aviso-' + (aviso.nivel || 'revisar')">
                                                            <i class="fas" :class="nivelIcono(aviso.nivel)"></i>
                                                            <span>
                                                                <span class="ev-nivel-etiqueta">{{ nivelLabel(aviso.nivel)
                                                                    }}</span>
                                                                {{ $t(aviso.codigo) }}<template v-if="aviso.detalle">:
                                                                    <strong>{{ aviso.detalle }}</strong></template>
                                                            </span>
                                                        </li>
                                                    </ul>
                                                    <small class="d-block text-muted">{{ textoEstadoVenta(venta) }}</small>
                                                    <button v-if="permisoEdicionBC && venta.editable && tieneAvisoEditable(venta)"
                                                        type="button" class="btn btn-sm btn-outline-primary mt-2"
                                                        @click="editarFormaPago(venta)">
                                                        <i class="fas fa-pencil-alt"></i> {{ $t("editarMedioPago") }}
                                                    </button>
                                                </div>

                                                <div v-if="showPaymentBreakdown(venta)" class="detalle-bloque">
                                                    <h6>{{ $t("desglosePagos") }}</h6>
                                                    <div class="fichas">
                                                        <span v-for="(pago, i) in venta.desglose"
                                                            :key="'pago-' + venta.id + '-' + i" class="ficha">
                                                            <span class="ficha-nombre">{{ pago.nombre }}</span>
                                                            <code v-if="pago.codigoBC" class="chip-bc">{{ pago.codigoBC
                                                                }}</code>
                                                            <span class="ficha-importe">{{ formatAmount(pago.importe)
                                                                }}</span>
                                                        </span>
                                                    </div>
                                                </div>

                                                <div v-if="venta.vales && venta.vales.length > 0" class="detalle-bloque">
                                                    <h6>
                                                        {{ $t("valesRegalo") }}
                                                        <small v-if="venta.valesDelDocumentoOriginal">({{
                                                            $t("valesDelDocumentoOriginal") }})</small>
                                                    </h6>
                                                    <div class="fichas">
                                                        <span v-for="vale in venta.vales" :key="'vale-' + vale.id"
                                                            class="ficha">
                                                            <span class="ficha-nombre">
                                                                {{ vale.codigoCupon }}
                                                                <small v-if="vale.descripcion" class="text-muted">{{
                                                                    vale.descripcion }}</small>
                                                            </span>
                                                            <span class="ficha-importe">{{ formatAmount(vale.importe)
                                                                }}</span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td></td>
                                    </tr>
                                </tbody>

                                <!-- Totales de todo lo filtrado, no solo de la pagina -->
                                <tbody>
                                    <tr class="fila-totales">
                                        <td></td>
                                        <td colspan="6" class="text-left">{{ $t("totalDeVenta") }}
                                            <small v-if="ventasVista.length !== filteredDaysToExport.length"
                                                class="text-muted">({{ ventasVista.length }} / {{
                                                    filteredDaysToExport.length }})</small>
                                        </td>
                                        <td class="text-right">{{ formatAmount(totalesVentas.totalImporte) }}</td>
                                        <td class="text-center col-comensales">{{
                                            totalesVentas.totalComensales }}</td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </template>

                            <tbody v-else>
                                <tr>
                                    <td colspan="10" class="text-center text-muted">
                                        {{ $t("evSinCoincidencias") }}
                                        <a v-if="hayFiltrosLocales" class="doc-enlace ml-2"
                                            @click="quitarFiltrosLocales()">{{ $t("evQuitarFiltros") }}</a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Paginacion en cliente: solo se pintan las filas de la pagina -->
                    <!-- Mismo aspecto que la de Productos BC: paginas centradas y, a la
                         derecha, el rango y cuantas por pagina -->
                    <div v-if="ventasVista.length > 0" class="ev-paginacion">
                        <div></div>

                        <nav v-if="totalPaginas > 1">
                            <ul class="pagination ev-pagination">
                                <li class="page-item" :class="{ disabled: pagina <= 1 }">
                                    <a class="page-link" href="#" @click.prevent="irAPagina(1)"
                                        :title="$t('evPrimeraPagina')">&laquo;</a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagina <= 1 }">
                                    <a class="page-link" href="#" @click.prevent="irAPagina(pagina - 1)">&lsaquo;</a>
                                </li>
                                <li class="page-item" v-for="n in paginasVisibles" :key="'pg-' + n"
                                    :class="{ active: n === pagina }">
                                    <a class="page-link" href="#" @click.prevent="irAPagina(n)">{{ n }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagina >= totalPaginas }">
                                    <a class="page-link" href="#" @click.prevent="irAPagina(pagina + 1)">&rsaquo;</a>
                                </li>
                                <li class="page-item" :class="{ disabled: pagina >= totalPaginas }">
                                    <a class="page-link" href="#" @click.prevent="irAPagina(totalPaginas)"
                                        :title="$t('evUltimaPagina')">&raquo;</a>
                                </li>
                            </ul>
                        </nav>
                        <div v-else></div>

                        <div class="ev-paginacion-info">
                            <span class="text-muted">{{ $t("evMostrandoRango", {
                                desde: rangoPagina.desde, hasta: rangoPagina.hasta, total: ventasVista.length
                            }) }}</span>
                            <select class="form-control ev-select ev-select-pagina" v-model.number="porPagina">
                                <option :value="25">25 {{ $t("evPorPagina") }}</option>
                                <option :value="50">50 {{ $t("evPorPagina") }}</option>
                                <option :value="100">100 {{ $t("evPorPagina") }}</option>
                            </select>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Advertencia previa a la exportacion. Mismo lenguaje visual que el
                 modal de detalle: tarjetas con titulo e icono -->
            <modal name="modal-export-data-warning" :adaptive="true" :scrollable="true" height="auto"
                width="640" :clickToClose="false">
                <div class="modal-detalles-forma-pago">
                    <div class="modal-header wrapperTitle">
                        <h4 style="margin: 0px">{{ $t("advertenciaExportacionVentas") }}</h4>
                        <span class="close-btn" @click="closeExportDataWarning()">&times;</span>
                    </div>

                    <div class="modal-body dv-body">
                        <!-- Que se exporta (informativo) -->
                        <div class="dv-seccion dv-seccion-info" style="margin-top: 0;">
                            <h6><i class="fas fa-info-circle"></i> {{ $t("evSeExportaraSeleccion") }}</h6>

                            <div class="ev-alcance">
                                <div class="ev-alcance-fila">
                                    <span class="ev-alcance-etiqueta">{{ $t("evDepartamentos") }}</span>
                                    <span class="ev-alcance-valor">{{ datosSeleccion.departamentos }}</span>
                                </div>
                                <div class="ev-alcance-fila">
                                    <span class="ev-alcance-etiqueta">{{ $t("evTpvsAExportar") }}</span>
                                    <div class="ev-alcance-tpvs">
                                        <span v-for="t in datosSeleccion.tpvs" :key="'alc-' + t.idTpv" class="ev-alcance-chip"
                                            :title="t.departamento">{{ t.nombre }}</span>
                                        <span v-if="!datosSeleccion.tpvs.length" class="ev-alcance-valor">-</span>
                                    </div>
                                </div>
                                <div class="ev-alcance-fila">
                                    <span class="ev-alcance-etiqueta">{{ $t("evAperturasAExportar") }}</span>
                                    <span class="ev-alcance-valor">{{ datosSeleccion.aperturas }}</span>
                                </div>
                                <div class="ev-alcance-fila">
                                    <span class="ev-alcance-etiqueta">{{ $t("evFacturasQR") }}</span>
                                    <span class="ev-alcance-valor">{{ datosSeleccion.qr }}</span>
                                </div>
                            </div>

                            <!-- Un envio por TPV o todos los TPV en el mismo envio -->
                            <div class="ev-modo-envio">
                                <span class="ev-modo-envio-titulo">{{ $t("evModoEnvio") }}</span>
                                <label class="ev-modo-envio-opcion" :class="{ 'ev-modo-envio-activa': envioPorTpv }">
                                    <input type="radio" :value="true" v-model="envioPorTpv" />
                                    <span>
                                        <strong>{{ $t("evEnvioPorTpv") }}</strong>
                                        <small>{{ $t("evEnvioPorTpvDesc") }}</small>
                                    </span>
                                </label>
                                <label class="ev-modo-envio-opcion" :class="{ 'ev-modo-envio-activa': !envioPorTpv }">
                                    <input type="radio" :value="false" v-model="envioPorTpv" />
                                    <span>
                                        <strong>{{ $t("evEnvioTodoJunto") }}</strong>
                                        <small>{{ $t("evEnvioTodoJuntoDesc") }}</small>
                                    </span>
                                </label>

                                <!-- Documentos por llamada a BC; personalizable solo para pruebas -->
                                <p class="ev-tramo-nota">{{ textoTramo }}</p>
                                <label class="ev-tramo-check">
                                    <input type="checkbox" v-model="tramoPersonalizado" />
                                    {{ $t("evPersonalizarTramos") }}
                                </label>
                                <div v-if="tramoPersonalizado" class="ev-tramo-opciones">
                                    <label class="ev-tramo-radio">
                                        <input type="radio" :value="true" v-model="tramoTodo" /> {{ $t("evTramoTodoDeUnaVez") }}
                                    </label>
                                    <label class="ev-tramo-radio">
                                        <input type="radio" :value="false" v-model="tramoTodo" /> {{ $t("evTramoDeN") }}
                                        <input type="number" min="50" max="5000" step="1" class="form-control ev-tramo-input"
                                            v-model.number="tamanoTramo" :disabled="tramoTodo" />
                                        {{ $t("evDocumentosCada") }}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Que va a pasar, en cifras. Solo se conoce lo del departamento
                             que se esta viendo (o del TPV elegido). -->
                        <div class="dv-seccion dv-lineas">
                            <h6><i class="fas fa-upload"></i> {{ $t("evQueSeEnviara") }}</h6>
                            <div class="ev-previo" style="margin-top: 0;">
                                <div class="ev-previo-cifra ev-previo-verde">
                                    <span>{{ $t("evSeEnviaran") }}</span>
                                    <strong>{{ resumenPrevio.aEnviar }}</strong>
                                    <small>{{ formatAmount(resumenPrevio.importeAEnviar) }}</small>
                                </div>
                                <div class="ev-previo-cifra" :class="{ 'ev-previo-rojo': resumenPrevio.bloqueantes > 0 }">
                                    <span>{{ $t("evNoSeExportaran") }}</span>
                                    <strong>{{ resumenPrevio.bloqueantes }}</strong>
                                    <small v-if="resumenPrevio.bloqueantes > 0">{{ $t("evQuedaranPendientes") }}</small>
                                </div>
                            </div>
                            <p class="ex-texto ex-texto-suave" style="margin: 8px 0 0;">
                                {{ $t("evCifrasDeLaSeleccion") }}
                                <template v-if="hayFiltrosLocales || modoAvanzadas !== 'todas'">
                                    {{ $t("evFiltrosNoAfectanExportacion") }}</template>
                            </p>
                        </div>

                        <p class="ex-pregunta">{{ $t("estaSeguroDeExportar") }}</p>
                    </div>

                    <div class="modal-footer">
                        <div class="d-flex justify-content-end col-12" style="gap: 12px;">
                            <!-- Solo con VUE_APP_BC_PREVIEW_RESPUESTA=1: modal de resultado con datos inventados -->
                            <button v-if="previewRespuestaActivo" type="button" class="ev-btn-modal ev-btn-modal-preview"
                                @click="previsualizarRespuesta()">
                                <i class="fas fa-eye"></i> {{ $t("evPrevisualizarRespuesta") }}
                            </button>
                            <button type="button" class="ev-btn-modal" @click="closeExportDataWarning()">
                                <i class="fas fa-times"></i> {{ $t("cancelar") }}
                            </button>
                            <button type="button" class="ev-btn-modal ev-btn-modal-primario" @click="exportDataBC()"
                                :disabled="exportando || resumenPrevio.aEnviar === 0">
                                <i class="fas fa-upload"></i> {{ $t("exportar") }}
                            </button>
                        </div>
                    </div>
                </div>
            </modal>

            <!-- Documentos ligados a la venta (ticket, factura, rectificativas) al excluir o incluir -->
            <modal name="modal-asociados-exportacion" :adaptive="true" :scrollable="true" height="auto" width="560"
                :clickToClose="false">
                <div class="modal-detalles-forma-pago">
                    <div class="modal-header wrapperTitle">
                        <h4 style="margin: 0px">{{ $t("evAsociadosTitulo") }}</h4>
                        <span class="close-btn" @click="resolverAsociados('cancelar')">&times;</span>
                    </div>

                    <div class="modal-body dv-body">
                        <p class="ev-asociados-texto">
                            {{ $t(asociadosModal.excluir ? "evAsociadosExcluirTexto" : "evAsociadosIncluirTexto",
                                { doc: asociadosModal.venta ? asociadosModal.venta.docNumber : '', n: asociadosModal.documentos.length }) }}
                        </p>
                        <ul class="ev-asociados-lista">
                            <li v-for="d in asociadosModal.documentos" :key="'asoc-' + d.id">
                                <span class="doc-numero">{{ d.docNumber }}</span>
                                <span class="ev-asociados-fecha">{{ d.fecha }}</span>
                                <span v-if="d.ref_docNumber" class="ev-asociados-ref">
                                    <i class="fas fa-reply"></i> {{ d.ref_docNumber }}
                                </span>
                                <span v-if="estadoLabel(d)" class="chip-estado" :class="'chip-estado-' + d.estadoExportacion">
                                    {{ estadoLabel(d) }}
                                </span>
                            </li>
                        </ul>
                        <p class="ex-pregunta">
                            {{ $t(asociadosModal.excluir ? "evAsociadosExcluirPregunta" : "evAsociadosIncluirPregunta") }}
                        </p>
                    </div>

                    <div class="modal-footer">
                        <div class="d-flex justify-content-end col-12" style="gap: 12px;">
                            <button type="button" class="ev-btn-modal" @click="resolverAsociados('cancelar')">
                                <i class="fas fa-times"></i> {{ $t("cancelar") }}
                            </button>
                            <button type="button" class="ev-btn-modal" @click="resolverAsociados('solo')">
                                {{ $t(asociadosModal.excluir ? "evExcluirSoloEste" : "evIncluirSoloEste") }}
                            </button>
                            <button type="button" class="ev-btn-modal ev-btn-modal-primario" @click="resolverAsociados('todos')">
                                {{ $t(asociadosModal.excluir ? "evExcluirTambienAsociados" : "evIncluirTambienAsociados") }}
                            </button>
                        </div>
                    </div>
                </div>
            </modal>

            <!-- Resultado de la exportacion, por TPV -->
            <!-- Sin resultado solo la lista de Excel; con resultado, la tanda y debajo la lista -->
            <modal name="modal-resultado-exportacion" :adaptive="true" :scrollable="true" height="auto"
                width="720" :clickToClose="true" @closed="recargarTrasResultado()">
                <div>
                    <div class="modal-header wrapperTitle">
                        <h4 v-if="resultadoExportacion" style="margin: 0px; display: flex; align-items: center; gap: 10px;">
                            <i class="fas" :class="{
                                'fa-check-circle ev-res-ok': resultadoExportacion.estado === 'ok',
                                'fa-exclamation-triangle ev-res-parcial': resultadoExportacion.estado === 'parcial',
                                'fa-times-circle ev-res-error': resultadoExportacion.estado === 'error',
                            }"></i>
                            {{ resultadoExportacion.estado === 'ok' ? $t("exportacionRealizada")
                                : (resultadoExportacion.estado === 'parcial' ? $t("exportacionRealizadaParcial") : $t("error")) }}
                        </h4>
                        <h4 v-else style="margin: 0px; display: flex; align-items: center; gap: 10px;">
                            <i class="fas fa-file-excel ev-res-ok"></i> {{ $t("evInformesExportacion") }}
                        </h4>
                        <span class="close-btn" @click="$modal.hide('modal-resultado-exportacion')">&times;</span>
                    </div>

                    <div class="modal-body">
                        <template v-if="resultadoExportacion">
                        <p v-if="resultadoExportacion.mensaje" class="ev-res-mensaje">{{ resultadoExportacion.mensaje }}</p>

                        <div class="ev-previo">
                            <div class="ev-previo-cifra ev-previo-verde">
                                <span>{{ $t("evExportados") }}</span>
                                <strong>{{ resultadoExportacion.totales.exportados }}</strong>
                            </div>
                            <div class="ev-previo-cifra" :class="{ 'ev-previo-rojo': resultadoExportacion.totales.fallidos }">
                                <span>{{ $t("evRechazadosPorBC") }}</span>
                                <strong>{{ resultadoExportacion.totales.fallidos }}</strong>
                            </div>
                            <div class="ev-previo-cifra" :class="{ 'ev-previo-ambar': resultadoExportacion.totales.omitidos }">
                                <span>{{ $t("evOmitidosPorValidacion") }}</span>
                                <strong>{{ resultadoExportacion.totales.omitidos }}</strong>
                            </div>
                        </div>

                        <!-- Las tarjetas hacen scroll; el bloque de informes queda fijo -->
                        <div class="ev-res-tpvs-scroll">
                        <div v-for="t in resultadoExportacion.tpvs" :key="'res-' + t.clave" class="ev-res-tpv">
                            <div class="ev-res-tpv-cabecera">
                                <strong>{{ t.nombre }}</strong>
                                <span class="ev-res-chip ev-res-chip-ok">{{ t.exportados }} {{ $t("evExportados").toLowerCase() }}</span>
                                <span v-if="t.fallidos.length" class="ev-res-chip ev-res-chip-error">{{ t.fallidos.length }} {{
                                    $t("evRechazadosPorBC").toLowerCase() }}</span>
                                <span v-if="t.omitidos.length" class="ev-res-chip ev-res-chip-ambar">{{ t.omitidos.length }} {{
                                    $t("evOmitidosPorValidacion").toLowerCase() }}</span>
                            </div>

                            <!-- Rechazadas por BC: documento y motivo; el resto, en el Excel -->
                            <ul v-if="t.fallidos.length" class="ev-res-omitidos ev-res-rechazadas">
                                <li v-for="(f, i) in primeros(t.fallidos)" :key="'f-' + t.clave + '-' + i">
                                    <a class="ev-res-doc" :class="{ 'doc-enlace': ventaDelResultado(t.idTpv, f.doc) }"
                                        @click="irADocumentoDelResultado(t.idTpv, f.doc)">{{ f.doc }}</a>
                                    <span class="text-muted">{{ f.motivo }}</span>
                                </li>
                                <li v-if="t.fallidos.length > LIMITE_RESULTADO" class="ev-res-mas">
                                    {{ $t("evMasEnExcel", { n: t.fallidos.length - LIMITE_RESULTADO }) }}
                                </li>
                            </ul>

                            <ul v-if="t.omitidos.length" class="ev-res-omitidos">
                                <li v-for="(o, i) in primeros(t.omitidos)" :key="'o-' + t.clave + '-' + i">
                                    <a class="ev-res-doc" :class="{ 'doc-enlace': ventaDelResultado(t.idTpv, o.doc) }"
                                        @click="irADocumentoDelResultado(t.idTpv, o.doc)">{{ o.doc }}</a>
                                    <span class="text-muted">{{ o.motivo }}</span>
                                </li>
                                <li v-if="t.omitidos.length > LIMITE_RESULTADO" class="ev-res-mas">
                                    {{ $t("evMasEnExcel", { n: t.omitidos.length - LIMITE_RESULTADO }) }}
                                </li>
                            </ul>
                        </div>
                        </div>

                        <small v-if="resultadoExportacion.totales.fallidos || resultadoExportacion.totales.omitidos"
                            class="d-block text-muted mt-2">{{ $t("evResultadoPendientesTexto") }}</small>
                        </template>

                        <!-- Informes Excel de la tanda, guardados en el servidor -->
                        <div class="ev-res-informes">
                            <div class="ev-res-informes-cabecera">
                                <strong><i class="fas fa-file-excel"></i> {{ $t("evResultadosExportacion") }}</strong>
                                <a class="ev-res-informes-refrescar" @click="cargarInformesExportacion()">
                                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': cargandoInformes }"></i>
                                    {{ $t("evActualizarInformes") }}
                                </a>
                            </div>

                            <p v-if="cargandoInformes && !informesVisibles.length" class="text-muted ev-res-informes-vacio">
                                <i class="fas fa-spinner fa-spin"></i> {{ $t("evCargandoInformes") }}
                            </p>

                            <p v-else-if="!informesVisibles.length" class="text-muted ev-res-informes-vacio">
                                {{ $t("evSinInformes") }}
                            </p>

                            <!-- Tras una tanda, solo su Excel; desde el enlace de informes, los ultimos -->
                            <ul v-else class="ev-res-informes-lista">
                                <li v-for="inf in informesVisibles" :key="'inf-' + inf.fichero"
                                    :class="{ 'ev-res-informe-actual': esInformeDeLaTanda(inf) }">
                                    <span class="ev-res-informe-fecha">{{ formatearFechaInforme(inf.fecha) }}</span>
                                    <!-- El total exportado viene en el nombre del fichero; sin el, no se sabe -->
                                    <span class="ev-res-informe-conteo" :class="{ 'text-muted': inf.exportados === null }">
                                        {{ inf.exportados !== null && inf.exportados !== undefined
                                            ? $t('evVentasExportadasN', { n: inf.exportados }) : $t('evConteoNoDisponible') }}
                                    </span>
                                    <span v-if="esInformeDeLaTanda(inf)" class="ev-res-chip ev-res-chip-ok">{{
                                        $t("evInformeEstaTanda") }}</span>
                                    <button type="button" class="ev-res-informe-descargar"
                                        @click="descargarInformeExportacion(inf.fichero)">
                                        <i class="fas fa-file-excel"></i> {{ $t("evDescargarInformeExcel") }}
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <div class="d-flex justify-content-end col-12">
                            <button class="btn btn-primary" style="background: #101255; border-color: #101255;"
                                @click="$modal.hide('modal-resultado-exportacion')">{{ $t("entendido") }}</button>
                        </div>
                    </div>
                </div>
            </modal>

            <!-- Editar el medio de pago de una venta -->
            <modal name="modal-forma-pago" :adaptive="true" :scrollable="true" height="auto" :clickToClose="false">
                <div>
                    <div class="modal-header wrapperTitle">
                        <h4 style="margin: 0px">{{ $t("editarMedioPago") }}</h4>
                        <span class="close-btn" @click="closeModalFormaPagoVisible()">&times;</span>
                    </div>

                    <div class="modal-body">
                        <!-- Qué venta se está tocando, para no editar a ciegas -->
                        <div class="resumen-venta-editada">
                            <div>
                                <span>{{ $t("documento") }}</span>
                                <strong>{{ ventaSeleccionada.docNumber || '-' }}</strong>
                            </div>
                            <div>
                                <span>{{ $t("importe") }}</span>
                                <strong>{{ formatAmount(ventaSeleccionada.importe) }}</strong>
                            </div>
                            <div>
                                <span>{{ $t("medioActual") }}</span>
                                <strong>{{ ventaSeleccionada.medioPago || '-' }}</strong>
                            </div>
                        </div>

                        <!-- Selector único: se elige el medio concreto y el tipo se deduce -->
                        <div class="form-group">
                            <label>{{ $t("medioDePago") }}</label>
                            <el-select v-model="medioPagoSeleccionado" class="select-default"
                                :placeholder="$t('seleccioneUnMedioPago')">
                                <el-option-group v-for="grupo in paymentMethodGroups" :key="grupo.tipo"
                                    :label="grupo.etiqueta">
                                    <el-option v-for="opcion in grupo.opciones" :key="opcion.id" :label="opcion.name"
                                        :value="opcion.id" :disabled="opcion.seleccionable === false">
                                        <span>{{ opcion.name }}</span>
                                        <span class="opcion-codigo">{{ opcion.code }}</span>
                                    </el-option>
                                </el-option-group>
                            </el-select>
                            <small class="d-block text-muted">{{ $t("importesNoEditables") }}</small>
                        </div>

                        <!-- PENDIENTE y CUENTA CASA se cobran contra alguien -->
                        <div v-if="paymentMethodRequiresClient" class="form-group">
                            <label>{{ $t("cliente") }} <span class="obligatorio">*</span></label>
                            <el-select v-model="clienteSeleccionado" class="select-default" filterable remote
                                reserve-keyword :remote-method="searchClients" :loading="buscandoClientes"
                                :placeholder="$t('buscarClientePorNombreCodigoODni')">
                                <el-option v-for="cliente in clientesEncontrados" :key="cliente.id"
                                    :label="clientLabel(cliente)" :value="cliente.id" />
                            </el-select>
                            <small class="d-block text-muted">{{ $t("medioPagoRequiereClienteTexto") }}</small>
                            <small v-if="selectedClientWithoutCode" class="d-block aviso-cliente-rojo">
                                <i class="fas fa-exclamation-triangle"></i> {{ $t("avisoSocioSinCodigo") }}
                            </small>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <div align="right" class="col-12">
                            <button @click="guardarFormaPagoSeleccionada()" class="btn btn-primary"
                                :style="!isFormaPagoChanged ? 'background-color:#ccc; color:#888; border-color:#ccc; cursor:not-allowed;' : ''"
                                :disabled="!isFormaPagoChanged || guardandoFormaPago">
                                <i class="fas fa-save"></i> {{ $t("guardarFormaPago") }}
                            </button>
                        </div>
                    </div>
                </div>
            </modal>

            <!-- Detalle de la venta: ficha compacta y, debajo, las lineas, que se
                 piden al abrir y se guardan para no volver a pedirlas -->
            <modal name="modal-detalles-forma-pago" :adaptive="true" :scrollable="true" height="auto"
                width="780" :clickToClose="true">
                <div class="modal-detalles-forma-pago" v-if="ventaDetallesSeleccionada">
                    <div class="modal-header wrapperTitle">
                        <h4 style="margin: 0px">{{ $t('detallesVenta') }}</h4>
                        <span class="close-btn" @click="closeModalDetallesFormaPago()">&times;</span>
                    </div>

                    <div class="modal-body dv-body">
                        <!-- Cabecera: documento a la izquierda, importe a la derecha -->
                        <div class="dv-cabecera">
                            <div>
                                <div class="doc-linea">
                                    <span class="badge-doc" :class="'badge-doc-' + (ventaDetallesSeleccionada.tipoDocumento || 'T')">
                                        {{ documentTypeLabel(ventaDetallesSeleccionada.tipoDocumento) }}
                                    </span>
                                    <span class="dv-doc">{{ ventaDetallesSeleccionada.docNumber || '-' }}</span>
                                    <span v-if="ventaDetallesSeleccionada.esFacturaQR" class="chip-origen">QR</span>
                                </div>
                                <small class="d-block text-muted mt-1">
                                    {{ ventaDetallesSeleccionada.fechaDocumento }} {{ ventaDetallesSeleccionada.horaDocumento }}
                                    · {{ ventaDetallesSeleccionada.tpvName || '-' }}
                                    · {{ ventaDetallesSeleccionada.workerName || '-' }}
                                </small>
                                <small v-if="ventaDetallesSeleccionada.esRectificativa" class="d-block dv-rectifica">
                                    <i class="fas fa-reply"></i> {{ $t('rectificaA') }}
                                    <strong>{{ ventaDetallesSeleccionada.documentoRectificado || '-' }}</strong>
                                </small>
                            </div>
                            <div class="dv-importe">
                                {{ formatAmount(ventaDetallesSeleccionada.importe) }}
                                <small v-if="amountSourceLabel(ventaDetallesSeleccionada)" class="d-block">{{
                                    amountSourceLabel(ventaDetallesSeleccionada) }}</small>
                            </div>
                        </div>

                        <div class="dv-columnas">
                            <!-- Cliente -->
                            <div class="dv-seccion dv-seccion-cliente">
                                <h6><i class="fas fa-user"></i> {{ $t('cliente') }}</h6>
                                <dl v-if="ventaDetallesSeleccionada.cliente" class="dv-datos">
                                    <dt>{{ $t('nombre') }}</dt>
                                    <dd>{{ ventaDetallesSeleccionada.cliente.nombreCompleto || '-' }}</dd>
                                    <dt>{{ $t('dni') }}</dt>
                                    <dd>
                                        {{ ventaDetallesSeleccionada.cliente.documento || '-' }}
                                        <small v-if="ventaDetallesSeleccionada.cliente.tipoDocumento" class="text-muted">({{
                                            ventaDetallesSeleccionada.cliente.tipoDocumento }})</small>
                                    </dd>
                                    <dt>{{ $t('codigoSocio') }}</dt>
                                    <dd>
                                        {{ ventaDetallesSeleccionada.cliente.codigo || '-' }}
                                        <span v-if="ventaDetallesSeleccionada.cliente.esSocio" class="badge-socio">{{ $t('socio') }}</span>
                                    </dd>
                                    <template v-if="ventaDetallesSeleccionada.cliente.email">
                                        <dt>{{ $t('email') }}</dt>
                                        <dd>{{ ventaDetallesSeleccionada.cliente.email }}</dd>
                                    </template>
                                    <template v-if="ventaDetallesSeleccionada.cliente.telefono">
                                        <dt>{{ $t('telefono') }}</dt>
                                        <dd>{{ ventaDetallesSeleccionada.cliente.telefono }}</dd>
                                    </template>
                                    <template v-if="ventaDetallesSeleccionada.cliente.direccion">
                                        <dt>{{ $t('direccion') }}</dt>
                                        <dd>{{ ventaDetallesSeleccionada.cliente.direccion }}</dd>
                                    </template>
                                </dl>
                                <p v-else class="text-muted dv-vacio">{{ $t('ventaSinCliente') }}</p>
                            </div>

                            <!-- Pago -->
                            <div class="dv-seccion dv-seccion-pago">
                                <h6><i class="fas fa-credit-card"></i> {{ $t('pago') }}</h6>
                                <dl class="dv-datos">
                                    <dt>{{ $t('formaDePago') }}</dt>
                                    <dd>{{ ventaDetallesSeleccionada.formaPago || ventaDetallesSeleccionada.tipoFormaPago || '-' }}</dd>
                                    <dt>{{ $t('medioDePago') }}</dt>
                                    <dd>
                                        <template v-if="ventaDetallesSeleccionada.desglose && ventaDetallesSeleccionada.desglose.length > 0">
                                            <span v-for="(pago, i) in ventaDetallesSeleccionada.desglose"
                                                :key="'detalle-pago-' + i" class="detalle-pago">
                                                {{ pago.nombre }}
                                                <code v-if="pago.codigoBC" class="chip-bc">{{ pago.codigoBC }}</code>
                                                <strong v-if="ventaDetallesSeleccionada.desglose.length > 1">{{
                                                    formatAmount(pago.importe) }}</strong>
                                            </span>
                                        </template>
                                        <template v-else>
                                            {{ ventaDetallesSeleccionada.medioPago || $t('sinDatos') }}
                                            <code v-if="ventaDetallesSeleccionada.codigoBC" class="chip-bc">{{
                                                ventaDetallesSeleccionada.codigoBC }}</code>
                                        </template>
                                    </dd>
                                    <template v-if="ventaDetallesSeleccionada.vales && ventaDetallesSeleccionada.vales.length > 0">
                                        <dt>{{ $t('valesRegalo') }}</dt>
                                        <dd>
                                            <span v-for="vale in ventaDetallesSeleccionada.vales" :key="'detalle-vale-' + vale.id"
                                                class="detalle-pago">
                                                {{ vale.codigoCupon }} <strong>{{ formatAmount(vale.importe) }}</strong>
                                            </span>
                                        </dd>
                                    </template>
                                    <!-- En las avanzadas el motivo es obvio: no se ensena -->
                                    <template v-if="!ventaDetallesSeleccionada.editable && ventaDetallesSeleccionada.paidType !== 'A'">
                                        <dt>{{ $t('edicion') }}</dt>
                                        <dd class="text-muted">{{ notEditableReason(ventaDetallesSeleccionada) }}</dd>
                                    </template>
                                </dl>
                            </div>
                        </div>

                        <!-- Lineas: se cargan al abrir y se quedan guardadas en la venta -->
                        <div class="dv-seccion dv-lineas">
                            <h6><i class="fas fa-list-ul"></i> {{ $t('evLineasVenta') }}
                                <small v-if="lineasVentaDetalle.estado === 'ok'" class="text-muted">({{
                                    lineasVentaDetalle.lineas.length }})</small>
                            </h6>

                            <div v-if="lineasVentaDetalle.estado === 'cargando'" class="dv-cargando">
                                <i class="fa fa-spinner fa-spin"></i> {{ $t('evCargandoLineas') }}
                            </div>

                            <div v-else-if="lineasVentaDetalle.estado === 'error'" class="dv-cargando">
                                {{ $t('evErrorCargandoLineas') }}
                                <a class="doc-enlace ml-2" @click="cargarLineasVenta(ventaDetallesSeleccionada, true)">{{
                                    $t('evVolverAIntentar') }}</a>
                            </div>

                            <p v-else-if="!lineasVentaDetalle.lineas.length" class="text-muted dv-vacio">{{ $t('evSinLineas') }}</p>

                            <table v-else class="table table-sm dv-tabla-lineas">
                                <thead>
                                    <tr>
                                        <th class="text-right dv-col-uds">{{ $t('evUds') }}</th>
                                        <th>{{ $t('producto') }}</th>
                                        <th class="dv-col-bc">{{ $t('evCodigoBC') }}</th>
                                        <th class="dv-col-unidad">{{ $t('evUnidadBC') }}</th>
                                        <th class="text-right">{{ $t('iva') }}</th>
                                        <th class="text-right">{{ $t('importe') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <!-- Un menu va seguido de sus platos (la API los agrupa): la fila del
                                         menu lleva el total y los platos van sangrados como "incluido" -->
                                    <tr v-for="(l, i) in lineasVentaDetalle.lineas" :key="'lin-' + i"
                                        :class="{ 'dv-linea-aviso': l.aviso, 'dv-linea-menu': l.esMenu, 'dv-linea-plato': l.esPlatoMenu }">
                                        <td class="text-right dv-col-uds">{{ l.unidades }}</td>
                                        <td class="dv-col-producto">
                                            <i v-if="l.esPlatoMenu" class="fas fa-level-up-alt fa-rotate-90 dv-plato-marca"></i>
                                            {{ l.nombre }}
                                            <span v-if="l.esMenu" class="dv-chip-menu">{{ $t('evMenu') }}</span>
                                            <small v-if="l.tarifa" class="text-muted"> · {{ l.tarifa }}</small>
                                            <small v-if="l.porcentDto" class="d-block text-muted">{{ $t('evDescuento') }} {{ l.porcentDto }}%</small>
                                        </td>
                                        <td class="dv-col-bc">
                                            <span v-if="l.esVale" class="dv-texto-suave">{{ $t('valesRegalo') }}</span>
                                            <code v-else-if="l.codigoBC" class="chip-bc dv-codigo">{{ l.codigoBC }}</code>
                                            <span v-if="l.aviso" class="dv-aviso-linea">
                                                <i class="fas fa-times-circle"></i> {{ $t(l.aviso) }}
                                            </span>
                                        </td>
                                        <!-- Unidad de medida en su propia columna: antes iba pegada al codigo -->
                                        <td class="dv-col-unidad">
                                            <span v-if="!l.esVale && l.unidadBC" class="dv-unidad">{{ l.unidadBC }}</span>
                                        </td>
                                        <td class="text-right">{{ l.iva }}%</td>
                                        <td class="text-right">
                                            <span v-if="l.esPlatoMenu" class="dv-incluido">{{ $t('evIncluidoEnMenu') }}</span>
                                            <strong v-else-if="l.esMenu">{{ formatAmount(l.importeMenu !== undefined ? l.importeMenu : l.importe) }}</strong>
                                            <template v-else>{{ formatAmount(l.importe) }}</template>
                                        </td>
                                    </tr>
                                </tbody>
                                <!-- Total de las lineas. Si no cuadra con el importe de la venta
                                     (vales, descuentos, cobro avanzado) se dice cuanto es este -->
                                <tfoot>
                                    <tr class="dv-total-lineas">
                                        <td class="text-right dv-col-uds"></td>
                                        <td colspan="4">{{ $t('evTotalLineas') }}</td>
                                        <td class="text-right">{{ formatAmount(totalLineasDetalle.importe) }}</td>
                                    </tr>
                                    <tr v-if="totalLineasDetalle.descuadre" class="dv-total-venta">
                                        <td></td>
                                        <td colspan="4">{{ $t('evImporteDeLaVenta') }}</td>
                                        <td class="text-right">{{ formatAmount(ventaDetallesSeleccionada.importe) }}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </modal>
        </div>
        <!-- <div v-if="showBottomSpacer" style="height: 25px; width: 100%;"></div> -->
        <div :class="['bottom-spacer', { 'bottom-spacer--visible': showBottomSpacer }]" />
    </div>
</template>

<script>
import { Loading } from 'element-ui';
import { DatePicker, Select, Option, OptionGroup, Dropdown, DropdownItem, DropdownMenu, Button } from 'element-ui';
import CabeceraBusinessCentral from "../../../../../components/CabeceraBusinessCentral/CabeceraBusinessCentral.vue";
import swal from 'sweetalert2';
import moment from "moment";

// Misma referencia para todas las ventas sin rectificativas
const SIN_RECTIFICATIVAS = Object.freeze([]);

// Gravedad de un nivel de aviso, para quedarse con el mas grave y ordenar los mismos.
// Sin nivel cuenta como 'revisar'.
function pesoNivel(nivel) {
    if (nivel === 'bloqueante') return 2;
    if (nivel === 'info') return 0;
    return 1;
}

export default {
    created() {
        this.getDeveloperModePermission();

        // Obtener idCompany de los parámetros de la ruta si está disponible
        if (this.$route.params.idCompany) {
            this.idCompany = this.$route.params.idCompany;
            // console.log('ID de compañía recibido por parámetro:', this.idCompany);
        }

        if (this.$route.params.listBusinesses) {
            this.listBusinesses = this.$route.params.listBusinesses;
            // console.log("hay negocios en los parámetros de la ruta");

            this.idBusiness = this.listBusinesses[0].id;

            // Primero el arbol (fija seleccion y negocio), despues la guarda de acceso
            this.cargarArbolExportacion(false).then(() => this.comprobarAccesoExportacion()).then((puedeEntrar) => {
                if (puedeEntrar) this.fetchDataToExport();
            });
        } else {
            // console.log("no hay negocios en los parámetros de la ruta");
            this.getCompanies()
                .then(() => this.cargarArbolExportacion(false))
                .then(() => this.comprobarAccesoExportacion())
                .then((puedeEntrar) => {
                    if (puedeEntrar) this.fetchDataToExport();
                });
        }

        if (this.$store.getters.idTpv) {
            this.idTpv = this.$store.getters.idTpv;
        }

        window.addEventListener('scroll', this.handleScroll);
    },
    destroyed() {
        // Elimino el listener cuando el componente se destruye
        window.removeEventListener('scroll', this.handleScroll);
        clearTimeout(this.temporizadorResaltado);
    },
    computed: {
        /* Opciones del selector único de medio de pago, agrupadas por forma de
           pago. El efectivo llega desde la API como una opción más con id 0. */
        paymentMethodGroups() {
            const etiquetas = {
                E: this.$t('efectivo'),
                T: this.$t('tarjeta'),
                C: this.$t('cheque'),
            };

            return ['E', 'T', 'C']
                .filter(tipo => Array.isArray(this.formasPagoPorTipo[tipo]) && this.formasPagoPorTipo[tipo].length > 0)
                .map(tipo => ({
                    tipo: tipo,
                    etiqueta: etiquetas[tipo] || tipo,
                    opciones: this.formasPagoPorTipo[tipo],
                }));
        },

        /* Opción elegida en el selector, con su specialType y si exige cliente. */
        selectedPaymentMethod() {
            if (this.medioPagoSeleccionado === null || this.medioPagoSeleccionado === undefined) return null;

            for (const grupo of this.paymentMethodGroups) {
                for (const opcion of grupo.opciones) {
                    if (opcion.id === this.medioPagoSeleccionado) return opcion;
                }
            }

            return null;
        },

        /* PENDIENTE (specialType 1) y CUENTA CASA (2) se cobran contra alguien. */
        paymentMethodRequiresClient() {
            return !!(this.selectedPaymentMethod && this.selectedPaymentMethod.requiereCliente);
        },

        selectedClientWithoutCode() {
            if (!this.clienteSeleccionado) return false;

            const cliente = this.clientesEncontrados.find(c => c.id === this.clienteSeleccionado);
            return !!(cliente && cliente.esSocio && cliente.sinCodigo);
        },

        isFormaPagoChanged() {
            if (!this.ventaSeleccionada || !this.ventaSeleccionada.id) return false;

            // Cambiar de medio de pago ya es un cambio.
            if (this.medioPagoSeleccionado !== this.medioPagoOriginal) {
                // Si el destino exige cliente, tiene que haber uno.
                if (this.paymentMethodRequiresClient && !this.clienteSeleccionado) return false;
                return true;
            }

            // Mismo medio, pero se le asigna o se le cambia el cliente.
            if (this.paymentMethodRequiresClient && this.clienteSeleccionado && this.clienteSeleccionado !== this.clienteOriginal) {
                return true;
            }

            return false;
        },

        filteredDaysToExport() {
            let ventas = this.daysToExport;

            if (this.modoAvanzadas === 'solo') {
                ventas = ventas.filter(v => (v.paidType || '') === 'A');
            } else if (this.modoAvanzadas === 'sin') {
                ventas = ventas.filter(v => (v.paidType || '') !== 'A');
            }

            return ventas;
        },

        totalesVentas() {
            let totalImporte = 0;
            let totalComensales = 0;

            // Sobre lo que se ve con los filtros de la barra, no solo la pagina
            this.ventasVista.forEach(v => {
                totalImporte += parseFloat(v.importe) || 0;
                totalComensales += parseInt(v.num_comensales) || 0;
            });

            return { totalImporte: totalImporte, totalComensales: totalComensales };
        },

        /* Lo que queda tras los filtros de las tarjetas, el buscador y el aviso elegido.
           Los chips de estado cuentan sobre esto; cada tarjeta, sobre todo menos su propio filtro */
        ventasFiltradas() {
            return this.filtrarLocal(this.filteredDaysToExport, '');
        },

        /* Ventas con avisos de verdad: las que solo tienen notas 'info' no cuentan */
        salesWithWarnings() {
            return this.ventasFiltradas.filter(v => this.hasWarnings(v));
        },

        ventasListas() {
            return this.ventasFiltradas.filter(v => this.esVentaLista(v));
        },

        ventasInformativas() {
            return this.ventasFiltradas.filter(v => this.nivelVenta(v) === 'info');
        },

        totalLineasDetalle() {
            const lineas = this.lineasVentaDetalle.lineas || [];
            const unidades = lineas.reduce((s, l) => s + (parseFloat(l.unidades) || 0), 0);
            const importe = lineas.reduce((s, l) => s + (parseFloat(l.importe) || 0), 0);
            const venta = this.ventaDetallesSeleccionada;
            const importeVenta = venta ? Math.abs(parseFloat(venta.importe) || 0) : 0;

            return {
                unidades: Math.round(unidades * 1000) / 1000,
                importe: importe,
                descuadre: Math.abs(Math.abs(importe) - importeVenta) >= 0.01,
            };
        },

        lineasVentaDetalle() {
            const venta = this.ventaDetallesSeleccionada;
            return (venta && this.lineasPorVenta[venta.id]) || { estado: 'cargando', lineas: [] };
        },

        /* ---------- Exportacion ---------- */

        /* En el modal de una tanda solo se ofrece su Excel */
        informesVisibles() {
            if (!this.resultadoExportacion || !this.informeTanda) return this.informesExportacion;
            return [this.informeTanda];
        },

        /* Boton de previsualizacion del resultado, solo en entornos con la flag */
        previewRespuestaActivo() {
            return String(process.env.VUE_APP_BC_PREVIEW_RESPUESTA || '') === '1';
        },

        /* La seleccion de los selectores ya no es la de la tabla */
        hayCambiosSinCargar() {
            return this.seleccionCargada !== null
                && this.claveSeleccion(this.seleccion) !== this.claveSeleccion(this.seleccionCargada);
        },

        /* Lo que se va a exportar, para el modal de confirmacion. Describe la
           seleccion CARGADA, que es la que viaja. */
        datosSeleccion() {
            const sel = this.seleccionCargada || this.seleccion;
            const deps = this.arbolExportacion.filter(d => sel.idBusinesses.indexOf(d.idBusiness) !== -1);
            const nombresDeps = sel.todos
                ? this.$t('evTodosLosDepartamentos') + ' (' + deps.length + ')'
                : deps.map(d => d.nombre).join(', ');

            const tpvs = [];
            deps.forEach(d => (d.tpvs || []).forEach(t => {
                if (sel.idTpvs.indexOf(t.idTpv) !== -1) tpvs.push({ idTpv: t.idTpv, nombre: t.nombre, departamento: d.nombre });
            }));

            let aperturas = this.$t('evAperturaMasAntiguaCadaTpv');
            if (tpvs.length === 1 && sel.idDays.length) {
                const tpv = deps.reduce((acc, d) => acc || (d.tpvs || []).find(t => t.idTpv === tpvs[0].idTpv), null);
                aperturas = ((tpv && tpv.aperturas) || [])
                    .filter(ap => sel.idDays.indexOf(ap.idDay) !== -1)
                    .map(ap => ap.apertura).join(', ');
            }

            return {
                departamentos: nombresDeps || '-',
                tpvs: tpvs,
                aperturas: aperturas,
                qr: sel.incluirQR ? this.$t('evQrIncluidas') : this.$t('evQrNoIncluidas'),
            };
        },

        /* Frase que explica cuantos documentos lleva cada llamada a BC con lo elegido */
        textoTramo() {
            if (!this.tramoPersonalizado) return this.$t('evTramoNotaDefecto');
            if (this.tramoTodo) return this.$t('evTramoNotaTodo');
            return this.$t('evTramoNota', { n: Math.max(50, parseInt(this.tamanoTramo, 10) || 100) });
        },

        /* Documentos por estado de exportacion, sobre lo que se ve con los filtros de arriba */
        contadoresEstado() {
            const c = { rechazados: 0, omitidos: 0, excluidos: 0 };
            this.ventasFiltradas.forEach(v => {
                if (v.estadoExportacion === 2) c.rechazados += 1;
                else if (v.estadoExportacion === 3) c.omitidos += 1;
                else if (v.estadoExportacion === 4) c.excluidos += 1;
            });
            return c;
        },

        /* Cifras del modal previo. La exportacion no mira los filtros de la
           tabla: se calcula sobre toda la seleccion. Las excluidas a mano no
           viajan, asi que no cuentan. */
        resumenPrevio() {
            const ventas = this.daysToExport.filter(v => v.estadoExportacion !== 4);

            let aEnviar = 0;
            let importeAEnviar = 0;
            let bloqueantes = 0;

            ventas.forEach(v => {
                if (this.nivelVenta(v) === 'bloqueante') {
                    bloqueantes += 1;
                } else {
                    aEnviar += 1;
                    importeAEnviar += parseFloat(v.importe) || 0;
                }
            });

            return { aEnviar: aEnviar, importeAEnviar: importeAEnviar, bloqueantes: bloqueantes };
        },

        /* ---------- Resumen de avisos ---------- */

        ventasBloqueantes() {
            return this.salesWithWarnings.filter(v => this.nivelVenta(v) === 'bloqueante');
        },

        totalFiltrado() {
            return this.filteredDaysToExport.reduce((suma, v) => suma + (parseFloat(v.importe) || 0), 0);
        },

        resumenAvisosClase() {
            return {
                'ev-resumen-avisos-bloqueante': this.ventasBloqueantes.length > 0,
                'ev-resumen-avisos-alerta': this.salesWithWarnings.length > 0 && this.ventasBloqueantes.length === 0,
            };
        },

        /* Avisos agrupados por codigo: cuantas ventas lo tienen y que detalles
           se repiten (p. ej. que productos estan sin mapear y en cuantas ventas). */
        avisosPorTipo() {
            const indice = {};

            this.filtrarLocal(this.filteredDaysToExport, 'aviso').forEach(venta => {
                if (!this.hasAvisos(venta)) return;

                const vistosEnVenta = {};

                venta.avisos.forEach(aviso => {
                    const codigo = aviso.codigo;

                    if (!indice[codigo]) {
                        indice[codigo] = { codigo: codigo, nivel: aviso.nivel || 'revisar', ventas: 0, detallesMapa: {} };
                    }

                    // El grupo toma el peor nivel de sus avisos
                    const grupo = indice[codigo];
                    if (pesoNivel(aviso.nivel) > pesoNivel(grupo.nivel)) grupo.nivel = aviso.nivel;

                    if (!vistosEnVenta[codigo]) {
                        vistosEnVenta[codigo] = true;
                        grupo.ventas += 1;
                    }

                    if (aviso.detalle) {
                        grupo.detallesMapa[aviso.detalle] = (grupo.detallesMapa[aviso.detalle] || 0) + 1;
                    }
                });
            });

            return Object.keys(indice)
                .map(codigo => {
                    const grupo = indice[codigo];
                    const detalles = Object.keys(grupo.detallesMapa)
                        .map(texto => ({ texto: texto, veces: grupo.detallesMapa[texto] }))
                        .sort((a, b) => b.veces - a.veces);

                    return { codigo: grupo.codigo, nivel: grupo.nivel, ventas: grupo.ventas, detalles: detalles };
                })
                .sort((a, b) => {
                    if (a.nivel !== b.nivel) return pesoNivel(b.nivel) - pesoNivel(a.nivel);
                    return b.ventas - a.ventas;
                });
        },

        /* ---------- Filtros locales de la tabla ---------- */

        chipsVista() {
            const base = this.ventasFiltradas;

            const chips = [
                { valor: 'todas', etiqueta: this.$t('evFiltroTodas'), num: base.length },
                { valor: 'listas', etiqueta: this.$t('evFiltroListas'), num: this.ventasListas.length },
                { valor: 'conAvisos', etiqueta: this.$t('evFiltroConAvisos'), num: this.salesWithWarnings.length },
                { valor: 'bloqueantes', etiqueta: this.$t('evFiltroBloqueantes'), num: this.ventasBloqueantes.length },
            ];

            // Estados de la ultima tanda: solo si hay alguno, para no llenar la barra
            const estados = this.contadoresEstado;
            if (estados.rechazados) chips.push({ valor: 'rechazados', etiqueta: this.$t('evEstadoRechazadoBC'), num: estados.rechazados });
            if (estados.omitidos) chips.push({ valor: 'omitidos', etiqueta: this.$t('evEstadoOmitido'), num: estados.omitidos });
            if (estados.excluidos) chips.push({ valor: 'excluidos', etiqueta: this.$t('evEstadoExcluido'), num: estados.excluidos });

            return chips;
        },

        hayFiltrosLocales() {
            return this.vista !== 'todas' || !!this.busqueda || !!this.filtroTpv || !!this.filtroTipoDoc
                || !!this.filtroAviso || !!this.filtroMedioResumen || this.modoAvanzadas !== 'todas';
        },

        nombreMedioResumen() {
            const fila = this.summaryByPaymentMethod.find(f => f.clave === this.filtroMedioResumen);
            return fila ? fila.nombre : this.filtroMedioResumen;
        },

        nombreFiltroTpv() {
            const fila = this.summaryByTpv.find(f => f.clave === this.filtroTpv);
            return fila ? fila.nombre : this.filtroTpv;
        },

        nombreFiltroTipoDoc() {
            return this.filtroTipoDoc ? this.documentTypeLabel(this.filtroTipoDoc) : '';
        },

        /* Lo que se ve en la tabla: filtros de la cabecera + los de la barra */
        ventasVista() {
            let ventas = this.ventasFiltradas;

            if (this.vista === 'listas') {
                ventas = ventas.filter(v => this.esVentaLista(v));
            } else if (this.vista === 'conAvisos') {
                ventas = ventas.filter(v => this.hasWarnings(v));
            } else if (this.vista === 'bloqueantes') {
                ventas = ventas.filter(v => this.nivelVenta(v) === 'bloqueante');
            } else if (this.vista === 'rechazados') {
                ventas = ventas.filter(v => v.estadoExportacion === 2);
            } else if (this.vista === 'omitidos') {
                ventas = ventas.filter(v => v.estadoExportacion === 3);
            } else if (this.vista === 'excluidos') {
                ventas = ventas.filter(v => v.estadoExportacion === 4);
            }

            if (this.orden.campo) {
                ventas = this.ordenarVentas(ventas);
            }

            return ventas;
        },

        /* Columnas del listado que se pueden ordenar, en el orden en que se pintan */
        columnasOrdenables() {
            return [
                { campo: 'fecha', etiqueta: 'evFechaDoc', clase: '' },
                { campo: 'tpv', etiqueta: 'tpv', clase: '' },
                { campo: 'documento', etiqueta: 'documento', clase: '' },
                { campo: 'cliente', etiqueta: 'cliente', clase: 'col-cliente' },
                { campo: 'formaPago', etiqueta: 'formaDePago', clase: '' },
                { campo: 'medioPago', etiqueta: 'medioDePago', clase: '' },
                { campo: 'importe', etiqueta: 'importe', clase: 'text-right' },
                { campo: 'comensales', etiqueta: 'nDeComensales', clase: 'text-center col-comensales' },
            ];
        },

        /* Cualquier cambio de filtro vuelve a la primera pagina */
        claveFiltros() {
            return [this.vista, this.busqueda, this.filtroTpv, this.filtroTipoDoc, this.filtroAviso,
                this.filtroMedioResumen, this.modoAvanzadas, this.porPagina,
                this.orden.campo, this.orden.dir].join('|');
        },

        /* ---------- Paginacion en cliente ---------- */

        totalPaginas() {
            return Math.max(1, Math.ceil(this.ventasVista.length / this.porPagina));
        },

        ventasPagina() {
            const inicio = (this.pagina - 1) * this.porPagina;
            return this.ventasVista.slice(inicio, inicio + this.porPagina);
        },

        /* Ventana de paginas alrededor de la actual (como en Productos BC) */
        paginasVisibles() {
            const max = 9;
            const total = this.totalPaginas;

            if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);

            let inicio = Math.max(1, this.pagina - Math.floor(max / 2));
            let fin = inicio + max - 1;

            if (fin > total) {
                fin = total;
                inicio = fin - max + 1;
            }

            return Array.from({ length: fin - inicio + 1 }, (_, i) => inicio + i);
        },

        rangoPagina() {
            const total = this.ventasVista.length;
            const desde = total === 0 ? 0 : (this.pagina - 1) * this.porPagina + 1;
            return { desde: desde, hasta: Math.min(total, this.pagina * this.porPagina) };
        },

        /* ---------- Rectificativas y originales ---------- */

        /* idTpv|docNumber -> venta, sobre todo el dia (no solo lo filtrado) */
        mapaDocumentos() {
            const mapa = {};

            this.daysToExport.forEach(v => {
                const doc = String(v.docNumber || '').trim();
                if (doc) mapa[v.idTpv + '|' + doc] = v;
            });

            return mapa;
        },

        /* idTpv|docNumber del original -> rectificativas que lo corrigen */
        mapaRectificativas() {
            const mapa = {};

            this.daysToExport.forEach(v => {
                const ref = String(v.documentoRectificado || '').trim();
                if (!v.esRectificativa || !ref) return;

                const clave = v.idTpv + '|' + ref;
                if (!mapa[clave]) mapa[clave] = [];
                mapa[clave].push(v);
            });

            return mapa;
        },

        /* Importe y número de documentos por medio de pago, que es como lo va a
           ver Business Central. Una venta avanzada aporta a cada una de sus patas. */
        summaryByPaymentMethod() {
            const acumulado = {};

            this.filtrarLocal(this.filteredDaysToExport, 'medio').forEach(venta => {
                const pagos = (venta.desglose && venta.desglose.length > 0)
                    ? venta.desglose
                    : [{ nombre: venta.medioPago || this.$t('sinDatos'), codigoBC: venta.codigoBC || '', importe: venta.importe }];

                pagos.forEach(pago => {
                    const clave = (pago.codigoBC || '') + '|' + (pago.nombre || '');

                    if (!acumulado[clave]) {
                        acumulado[clave] = {
                            clave: clave,
                            nombre: pago.nombre || this.$t('sinDatos'),
                            codigoBC: pago.codigoBC || '',
                            importe: 0,
                            documentos: 0,
                        };
                    }

                    acumulado[clave].importe += parseFloat(pago.importe) || 0;
                    acumulado[clave].documentos += 1;
                });
            });

            return Object.keys(acumulado)
                .map(k => acumulado[k])
                .sort((a, b) => Math.abs(b.importe) - Math.abs(a.importe));
        },

        summaryByTpv() {
            const acumulado = {};

            this.filtrarLocal(this.filteredDaysToExport, 'tpv').forEach(venta => {
                const clave = String(venta.idTpv);

                if (!acumulado[clave]) {
                    acumulado[clave] = {
                        clave: clave,
                        nombre: venta.tpvName || ('TPV ' + venta.idTpv),
                        importe: 0,
                        documentos: 0,
                    };
                }

                acumulado[clave].importe += parseFloat(venta.importe) || 0;
                acumulado[clave].documentos += 1;
            });

            return Object.keys(acumulado)
                .map(k => acumulado[k])
                .sort((a, b) => Math.abs(b.importe) - Math.abs(a.importe));
        },

        summaryByDocumentType() {
            const acumulado = {};

            this.filtrarLocal(this.filteredDaysToExport, 'tipoDoc').forEach(venta => {
                const clave = venta.tipoDocumento || 'T';

                if (!acumulado[clave]) {
                    acumulado[clave] = {
                        clave: clave,
                        nombre: this.documentTypeLabel(clave),
                        importe: 0,
                        documentos: 0,
                    };
                }

                acumulado[clave].importe += parseFloat(venta.importe) || 0;
                acumulado[clave].documentos += 1;
            });

            return ['T', 'F', 'R']
                .filter(tipo => acumulado[tipo])
                .map(tipo => acumulado[tipo]);
        },
    },
    components: {
        CabeceraBusinessCentral,
        [DatePicker.name]: DatePicker,
        [Select.name]: Select,
        [Dropdown.name]: Dropdown,
        [DropdownItem.name]: DropdownItem,
        [DropdownMenu.name]: DropdownMenu,
        [Button.name]: Button,
        [Option.name]: Option,
        [OptionGroup.name]: OptionGroup
    },
    data() {
        return {
            listBusinesses: [],
            listTpv: [],
            daysToExport: [],
            idBusiness: null,
            // Restricción 80 (permisiva): permite cambiar el medio de pago de una
            // venta. Los importes no se editan desde aquí en ningún caso.
            permisoEdicionBC: false,
            // Modo desarrollador (config 133): habilita el botón de refrescar
            developerMode: false,
            refrescando: false,
            idTpv: null,
            idCompany: null,

            // UTILS
            exportDate: null,
            hasPendingExports: false,
            previousDate: null,

            // BOTÓN DE EXPORTAR
            showFloatingBtn: false,
            isContentShort: false,
            showBottomSpacer: false,

            // PAGOS
            formasPagoPorTipo: {
                E: [],
                T: [],
                C: []
            },
            ventaSeleccionada: {
                formaPago: null
            },
            // Selector único: se elige el medio de pago concreto y el tipo se deduce.
            medioPagoSeleccionado: null,
            medioPagoOriginal: null,
            // Cliente, para los medios de pago que lo exigen (PENDIENTE, CUENTA CASA)
            clienteSeleccionado: null,
            clienteOriginal: null,
            clientesEncontrados: [],
            buscandoClientes: false,
            guardandoFormaPago: false,

            // Filas desplegadas de la tabla
            filasAbiertas: {},

            // Ventas con varios pagos en la tarjeta de medios de pago y en la tabla:
            // 'todas' | 'solo' | 'sin'
            modoAvanzadas: 'todas',

            // Modal detalles de pago
            ventaDetallesSeleccionada: null,

            // Arbol departamentos -> TPV -> aperturas y seleccion del usuario
            arbolExportacion: [],
            seleccion: { todos: false, idBusinesses: [], idTpvs: [], idDays: [], incluirQR: true },
            cambiandoExclusion: {},
            // Seleccion y estado de la tanda cuya lista hay que recargar al cerrar el modal de resultado
            recargaPendiente: null,
            // Venta que se excluye o incluye y sus documentos asociados, para el modal
            asociadosModal: { venta: null, excluir: true, documentos: [] },
            // Seleccion con la que se cargo la lista: es la que viaja en la exportacion
            seleccionCargada: null,
            // true: un envio por TPV; false: todos los TPV elegidos en un mismo envio
            envioPorTpv: true,
            // Documentos por llamada a BC. Sin personalizar, la API usa 100.
            tramoPersonalizado: false,
            tramoTodo: false,
            tamanoTramo: 100,
            exportando: false,
            // Resultado de la ultima exportacion, para su modal
            resultadoExportacion: null,
            // Documentos rechazados u omitidos que se listan por tarjeta; el resto, al Excel
            LIMITE_RESULTADO: 3,
            // Informes Excel de exportacion de la company (los ultimos que guarda la API)
            informesExportacion: [],
            informeTanda: null, // el que ha devuelto la ultima exportacion
            cargandoInformes: false,

            // Cuentas sin cobrar (status P) del dia: informativo (no se exportan)
            cuentasSinCobrar: { documentos: 0, importe: 0 },

            // Lineas ya pedidas para el modal de detalle: idVenta -> { estado, lineas }
            lineasPorVenta: {},

            // Carga del dia pendiente
            cargandoVentas: false,
            // Ya hay respuesta para el negocio actual: hasta entonces no se puede
            // afirmar que no haya nada que exportar
            consultaResuelta: false,
            errorCargaVentas: false,

            // Resumen de avisos
            avisosDesplegados: false,

            // Filtros locales de la tabla (no van al servidor)
            busqueda: '',
            vista: 'todas', // 'todas' | 'conAvisos' | 'bloqueantes'
            filtroTpv: '',
            filtroTipoDoc: '',
            filtroAviso: '',
            filtroMedioResumen: '',

            // Orden del listado. Sin campo, el orden en que llegan de la API
            orden: { campo: '', dir: 1 },

            // Paginacion en cliente
            pagina: 1,
            porPagina: 25,

            // Fila resaltada tras saltar a un documento
            resaltadoId: null,
            temporizadorResaltado: null,
        };
    },
    watch: {
        claveFiltros() {
            this.pagina = 1;
        },
        // Al recargar (p. ej. tras editar un medio de pago) puede haber menos paginas
        totalPaginas(total) {
            if (this.pagina > total) this.pagina = total;
        },
    },
    methods: {

        /* Departamentos, TPV y aperturas pendientes. Se recarga tras cada exportacion. */
        async cargarArbolExportacion(mantenerSeleccion = true) {
            const idCompany = this.idCompany || this.$route.params.idCompany;
            if (!idCompany) return;

            try {
                const response = await this.axios.get(
                    this.$store.getters.apiUrl + "businesscentral/getAperturasPendientes",
                    {
                        params: { idCompany: idCompany, jwt: this.$store.getters.jwt },
                        headers: { Authorization: "Bearer " + this.$store.getters.jwt },
                    }
                );

                const data = (response && response.data) ? response.data : {};
                this.arbolExportacion = Array.isArray(data.result) ? data.result : [];
            } catch (error) {
                console.error("Error al cargar los departamentos y aperturas pendientes:", error);
                this.arbolExportacion = [];
            }

            const idsDeps = this.arbolExportacion.map(d => d.idBusiness);
            const sel = this.seleccion;
            const seleccionValida = mantenerSeleccion && sel.idBusinesses.some(id => idsDeps.indexOf(id) !== -1);

            if (!seleccionValida) {
                // Se arranca con todos los departamentos y todos sus TPV
                this.seleccion = {
                    todos: true,
                    idBusinesses: idsDeps,
                    idTpvs: this.tpvsDeDepartamentos(idsDeps),
                    idDays: [],
                    incluirQR: true,
                };
            } else if (sel.todos) {
                this.seleccion = Object.assign({}, sel, { idBusinesses: idsDeps, idTpvs: this.tpvsDeDepartamentos(idsDeps) });
            }

            this.idBusiness = this.seleccion.idBusinesses.length ? this.seleccion.idBusinesses[0] : this.idBusiness;

            // Sin arbol hace falta igualmente un negocio para la comprobacion de acceso
            if (!this.idBusiness && Array.isArray(this.listBusinesses) && this.listBusinesses.length) {
                this.idBusiness = this.listBusinesses[0].id;
            }
        },

        pendientesDepartamento(dep) {
            return (dep.tpvs || []).reduce((s, tpv) => {
                const aperturas = (tpv.aperturas || []).reduce((a, ap) => a + (ap.aExportar || 0), 0);
                return s + aperturas + ((tpv.qr && tpv.qr.aExportar) || 0);
            }, 0);
        },

        tpvsDeDepartamentos(idsBusinesses) {
            const ids = [];
            this.arbolExportacion.forEach(dep => {
                if (idsBusinesses.indexOf(dep.idBusiness) === -1) return;
                (dep.tpvs || []).forEach(tpv => ids.push(tpv.idTpv));
            });
            return ids;
        },

        /* Negocio de un TPV, para las llamadas que van por negocio */
        negocioDeTpv(idTpv) {
            const dep = this.arbolExportacion.find(d => (d.tpvs || []).some(t => t.idTpv === Number(idTpv)));
            return dep ? dep.idBusiness : this.idBusiness;
        },

        /* La lista se carga al pulsar "Cargar ventas", no al cambiar los selectores */
        onSeleccionChanged(nueva) {
            this.seleccion = nueva;
        },

        async aplicarSeleccion() {
            if (this.cargandoVentas) return;

            this.showFloatingBtn = false;

            // Los datos y filtros de la seleccion anterior no valen para esta
            this.daysToExport = [];
            this.lineasPorVenta = {};
            this.consultaResuelta = false;
            this.errorCargaVentas = false;
            this.filasAbiertas = {};
            this.quitarFiltrosLocales();

            // El permiso se reevalua con el primer departamento elegido
            this.idBusiness = this.seleccion.idBusinesses.length ? this.seleccion.idBusinesses[0] : this.idBusiness;

            const puedeEntrar = await this.comprobarAccesoExportacion();
            if (puedeEntrar) await this.fetchDataToExport();
        },

        /* Copia plana de una seleccion, con las listas ordenadas, para compararlas */
        claveSeleccion(sel) {
            if (!sel) return '';
            const orden = lista => (lista || []).map(Number).sort((a, b) => a - b).join(',');
            return [sel.todos ? 1 : 0, orden(sel.idBusinesses), orden(sel.idTpvs), orden(sel.idDays), sel.incluirQR ? 1 : 0].join('|');
        },

        estadoLabel(venta) {
            if (venta.estadoExportacion === 2) return this.$t('evEstadoRechazadoBC');
            if (venta.estadoExportacion === 3) return this.$t('evEstadoOmitido');
            if (venta.estadoExportacion === 4) return this.$t('evEstadoExcluido');
            return '';
        },

        /* Excluir una venta de la exportacion (estado 4) o volver a incluirla */
        async toggleExclusion(venta) {
            if (!venta || this.cambiandoExclusion[venta.id]) return;

            const excluir = venta.estadoExportacion !== 4;
            this.$set(this.cambiandoExclusion, venta.id, true);

            try {
                // Al excluir se ofrecen los asociados que aun viajan; al incluir, los excluidos
                const asociados = (await this.cargarAsociadosExportacion(venta))
                    .filter(d => excluir ? d.estadoExportacion !== 4 : d.estadoExportacion === 4);

                if (asociados.length) {
                    this.asociadosModal = { venta: venta, excluir: excluir, documentos: asociados };
                    this.$modal.show('modal-asociados-exportacion');
                    return;
                }

                await this.aplicarExclusion(venta, excluir, [venta.id]);
            } catch (error) {
                this.$set(this.cambiandoExclusion, venta.id, false);
                this.avisarErrorExclusion(error);
            }
        },

        async cargarAsociadosExportacion(venta) {
            const response = await this.axios.get(
                this.$store.getters.apiUrl + "businesscentral/asociadosVentaExportacion",
                {
                    params: {
                        jwt: this.$store.getters.jwt,
                        idBusiness: this.negocioDeTpv(venta.idTpv),
                        idVenta: venta.id,
                        idTpv: venta.idTpv,
                    },
                    headers: { Authorization: "Bearer " + this.$store.getters.jwt },
                }
            );

            const datos = (response && response.data) ? response.data : {};
            if (!datos.response) throw new Error(datos.message || this.$t('errorInesperado'));

            return Array.isArray(datos.result) ? datos.result : [];
        },

        async resolverAsociados(opcion) {
            const { venta, excluir, documentos } = this.asociadosModal;
            this.$modal.hide('modal-asociados-exportacion');
            if (!venta) return;

            if (opcion === 'cancelar') {
                this.$set(this.cambiandoExclusion, venta.id, false);
                return;
            }

            const ids = opcion === 'todos' ? [venta.id].concat(documentos.map(d => d.id)) : [venta.id];

            try {
                await this.aplicarExclusion(venta, excluir, ids);
            } catch (error) {
                this.$set(this.cambiandoExclusion, venta.id, false);
                this.avisarErrorExclusion(error);
            }
        },

        /* Excluye o incluye las ventas indicadas (todas del TPV de la venta) y actualiza las filas en sitio */
        async aplicarExclusion(venta, excluir, ids) {
            const response = await this.axios.post(
                this.$store.getters.apiUrl + "businesscentral/excluirVentaExportacion",
                {},
                {
                    params: {
                        jwt: this.$store.getters.jwt,
                        idBusiness: this.negocioDeTpv(venta.idTpv),
                        idTpv: venta.idTpv,
                        idVentas: ids.join(','),
                        excluir: excluir,
                    },
                    headers: { Authorization: "Bearer " + this.$store.getters.jwt },
                }
            );

            const datos = (response && response.data) ? response.data : {};
            if (!datos.response) throw new Error(datos.message || this.$t('errorInesperado'));

            const cambiadas = (datos.result && Array.isArray(datos.result.cambiadas))
                ? datos.result.cambiadas
                : [{ idHeader: venta.id, idTpv: venta.idTpv, docNumber: venta.docNumber, estadoExportacion: excluir ? 4 : 0 }];

            cambiadas.forEach(c => {
                const fila = this.daysToExport.find(v => v.id === c.idHeader && String(v.idTpv) === String(c.idTpv));
                if (fila) this.aplicarEstadoEnFila(fila, Number(c.estadoExportacion));
                this.refrescarAvisoOriginal(c.docNumber, c.idTpv, Number(c.estadoExportacion) === 4);
            });

            this.$notify({
                message: cambiadas.length > 1
                    ? this.$t(excluir ? 'evVentasExcluidasN' : 'evVentasIncluidasN', { n: cambiadas.length })
                    : this.$t(excluir ? 'evVentaExcluida' : 'evVentaIncluida'),
                icon: "nc-icon nc-check-2",
                timeout: 2500,
                horizontalAlign: "right",
                verticalAlign: "top",
                type: "success",
            });

            this.$set(this.cambiandoExclusion, venta.id, false);
            this.cargarArbolExportacion().catch(() => {});
        },

        aplicarEstadoEnFila(fila, estadoNuevo) {
            this.$set(fila, 'estadoExportacion', estadoNuevo);

            let avisos = (fila.avisos || []).filter(a => a.codigo !== 'avisoExcluidaManual' && a.codigo !== 'avisoOriginalNoExportado');
            let retenida = '';

            if (estadoNuevo === 4) {
                avisos.push({ codigo: 'avisoExcluidaManual', detalle: '', nivel: 'revisar' });
            } else {
                const ref = String(fila.ref_docNumber || '').trim();
                const original = ref ? this.daysToExport.find(v => String(v.idTpv) === String(fila.idTpv) && String(v.docNumber).trim() === ref) : null;
                const originalDisponible = !ref || fila.documentoRectificadoExportado || (original && original.estadoExportacion !== 4);

                if (!originalDisponible) {
                    avisos.push({ codigo: 'avisoOriginalNoExportado', detalle: ref, nivel: 'bloqueante' });
                    retenida = ref;
                }
            }

            this.$set(fila, 'avisos', avisos);
            this.$set(fila, 'retenidaPorOriginal', retenida);
        },

        /* Las filas que referencian a un documento excluido no pueden viajar: aviso bloqueante en sitio */
        refrescarAvisoOriginal(docNumber, idTpv, excluido) {
            this.daysToExport
                .filter(v => String(v.idTpv) === String(idTpv) && String(v.ref_docNumber || '').trim() === String(docNumber).trim())
                .forEach(v => {
                    const avisos = (v.avisos || []).filter(a => a.codigo !== 'avisoOriginalNoExportado');
                    if (excluido) avisos.push({ codigo: 'avisoOriginalNoExportado', detalle: docNumber, nivel: 'bloqueante' });
                    this.$set(v, 'avisos', avisos);
                    this.$set(v, 'retenidaPorOriginal', excluido ? docNumber : '');
                });
        },

        avisarErrorExclusion(error) {
            console.error("Error al cambiar la exclusion de la venta:", error);
            swal({
                title: this.$t("atencion"),
                text: (error && error.message) || this.$t('errorInesperado'),
                confirmButtonClass: "btn btn-info btn-fill",
                type: "warning",
            });
        },

        // onDateChangeAttempt(newDate) {
        //     this.exportDate = newDate;
        //     this.fetchDataToExport();
        // },

        getFormaPagoNombre(letra) {
            switch (letra) {
                case 'E': return 'EFECTIVO';
                case 'T': return 'TARJETA';
                case 'C': return 'CHEQUE';
                default: return '';
            }
        },

        handleScroll() {
            const scrollPosition = window.scrollY;
            const pageHeight = document.documentElement.scrollHeight;
            const windowHeight = window.innerHeight;

            // Si el usuario está cerca del final de la página, oculto el botón para 
            // que pueda ver datos - Siempre que no haya un filtro de avanzadas activo
            if (this.modoAvanzadas === 'todas') {
                if (scrollPosition + windowHeight >= pageHeight - 30) {
                    this.showBottomSpacer = true;
                    this.isContentShort = true;
                    // console.log("D: " + this.showFloatingBtn);
                } else {
                    this.showBottomSpacer = false;
                    this.isContentShort = false;
                    // console.log("E: " + this.showFloatingBtn);
                }
            }
        },

        async showExportDataWarning() {
            // Con cambios sin cargar se recarga primero y el modal se abre con los datos nuevos
            if (this.hayCambiosSinCargar) {
                const cargando = Loading.service({ lock: true, text: this.$t("evActualizandoVentas") });

                try {
                    await this.aplicarSeleccion();
                } finally {
                    cargando.close();
                }

                // Si la seleccion nueva no tiene nada que exportar, la pagina ya lo dice
                if (!this.daysToExport.length) return;
            }

            this.$modal.show('modal-export-data-warning');
        },

        closeExportDataWarning() {
            this.$modal.hide('modal-export-data-warning');
        },

        verDetallesFormaPago(venta) {
            this.ventaDetallesSeleccionada = venta;
            this.$modal.show('modal-detalles-forma-pago');
            this.cargarLineasVenta(venta);
        },

        /* Lineas de la venta para el modal. Se piden una vez por venta y se
           guardan: al volver a abrirla ya estan. */
        async cargarLineasVenta(venta, forzar = false) {
            if (!venta || !venta.id) return;

            const guardado = this.lineasPorVenta[venta.id];
            if (guardado && !forzar && (guardado.estado === 'ok' || guardado.estado === 'cargando')) return;

            this.$set(this.lineasPorVenta, venta.id, { estado: 'cargando', lineas: [] });

            try {
                const response = await this.axios.get(
                    this.$store.getters.apiUrl + "businesscentral/getSaleLinesForExport",
                    {
                        params: {
                            jwt: this.$store.getters.jwt,
                            // El negocio de la venta, que puede no ser el primero elegido
                            idBusiness: this.negocioDeTpv(venta.idTpv),
                            idVenta: venta.id,
                        },
                        headers: { Authorization: "Bearer " + this.$store.getters.jwt },
                    }
                );

                const datos = response.data || {};

                this.$set(this.lineasPorVenta, venta.id, datos.response
                    ? { estado: 'ok', lineas: datos.result || [] }
                    : { estado: 'error', lineas: [] });
            } catch (error) {
                console.error("Error cargando las lineas de la venta:", error);
                this.$set(this.lineasPorVenta, venta.id, { estado: 'error', lineas: [] });
            }
        },

        closeModalDetallesFormaPago() {
            this.$modal.hide('modal-detalles-forma-pago');
        },

        openModalFormaPagoVisible() {
            this.$modal.show('modal-forma-pago');
        },

        closeModalFormaPagoVisible() {
            this.$modal.hide('modal-forma-pago');
        },

        /* Sin argumento usa la seleccion de los selectores; las recargas pasan la ya cargada */
        async fetchDataToExport(seleccionAUsar = null) {
            // Ocultar el botón de exportación
            this.showFloatingBtn = false;
            this.hasPendingExports = false;

            // La lista nueva arranca sin filtro de avanzadas
            this.modoAvanzadas = 'todas';

            if (!this.idBusiness) return;

            const seleccion = JSON.parse(JSON.stringify(seleccionAUsar || this.seleccion));

            const config = {
                params: {
                    jwt: this.$store.getters.jwt,
                    idBusiness: this.idBusiness,
                    idCompany: this.idCompany || this.$route.params.idCompany,
                    idTpvs: seleccion.idTpvs.join(','),
                    idDays: seleccion.idDays.join(','),
                    incluirQR: seleccion.incluirQR,
                },
                headers: {
                    Authorization: "Bearer " + this.$store.getters.jwt
                },
            };

            this.cargandoVentas = true;
            this.errorCargaVentas = false;

            try {
                const response = await this.axios.get(
                    this.$store.getters.apiUrl + "businesscentral/checkDaysAndGetHeadersToExport",
                    config
                );

                // Lo que hay en la tabla corresponde a esta seleccion
                this.seleccionCargada = seleccion;

                if (response.data.response && response.data.result.length > 0) {
                    this.daysToExport = response.data.result;
                    this.hasPendingExports = true;
                    this.showFloatingBtn = true;

                    this.isContentShort = this.daysToExport.length < 8 ? true : false;

                    const fechaExportacion = response.data.fechaExportacion;
                    this.exportDate = fechaExportacion;
                    this.cuentasSinCobrar = response.data.cuentasSinCobrar || { documentos: 0, importe: 0 };
                } else {
                    this.hasPendingExports = false;
                    this.showFloatingBtn = false;
                    this.daysToExport = [];
                    this.exportDate = null;
                    this.cuentasSinCobrar = { documentos: 0, importe: 0 };

                    this.errorCargaVentas = !response.data || response.data.response === undefined;
                }
            } catch (error) {
                console.error("Error al obtener días para exportar:", error);
                this.errorCargaVentas = true;
            } finally {
                this.cargandoVentas = false;
                this.consultaResuelta = true;
            }
        },

        getCompanies() {
            var config = {
                params: {
                    jwt: this.$store.getters.jwt
                },
                headers: { 'Authorization': "Bearer " + this.$store.getters.jwt }
            };

            this.loadingGeneral = Loading.service({ text: this.$t('cargando') });

            let vm = this;
            // Se devuelve la promesa para que quien llame (refrescarDatos) pueda esperarla.
            return this.axios.get(this.$store.getters.apiUrl + "general/getCompaniesByUser", config)
                .then(function (response) {

                    if (!vm.idCompany) {
                        vm.idCompany = response.data.result[0].id;
                        //console.log(" > La Company seleccionada es:", vm.idCompany,);
                    }
                    vm.listCompany = response.data.result;

                    // console.log(" > Lista de Companies actualizada:", vm.listCompany);
                    const pendiente = vm.getBusinessListByCompany();

                    vm.loadingGeneral.close();

                    return pendiente;

                })
                .catch(function (error) {
                    console.error(error);
                });
        },

        getBusinessListByCompany() {
            var config = {
                params: {
                    idCompany: this.idCompany,
                    jwt: this.$store.getters.jwt
                },
                headers: { 'Authorization': "Bearer " + this.$store.getters.jwt }
            };
            // console.log("El id company es:", this.idCompany);
            let vm = this;
            // Idem: se devuelve la promesa para poder encadenarla.
            return this.axios.get(this.$store.getters.apiUrl + "businesses/getBusinessesByIdCompany", config)
                .then(function (response) {

                    vm.listBusinesses = response.data;
                    // console.log("> La listBusinesses by Company es:", vm.listBusinesses,);
                    vm.loadingGeneral.close();

                    // Sin negocios no se va a pedir nada: se da la consulta por resuelta
                    // para no quedarse en "cargando" para siempre
                    if (!Array.isArray(vm.listBusinesses) || vm.listBusinesses.length === 0) {
                        vm.consultaResuelta = true;
                    }

                    // Si vengo de un redirect, le notifico al usuario por qué ha sido redirigido a Integraciones.
                    if (vm.mostrarNotificacionRedirect) {
                        vm.checkRedirect();
                    }

                })
                .catch(function (error) {
                    console.error(error);
                    vm.errorCargaVentas = true;
                    vm.consultaResuelta = true;
                });
        },

        async exportDataBC() {
            // Una exportacion cada vez: el boton flotante seguia activo mientras
            // duraba la peticion y se podia lanzar dos veces
            if (this.exportando) return;

            // Viaja la seleccion con la que se cargo la tabla, no la de los selectores
            const seleccion = this.seleccionCargada || this.seleccion;

            if (!seleccion.idTpvs.length || this.hayCambiosSinCargar) return;

            this.closeExportDataWarning();
            var timestamp = moment(this.exportDate, "DD-MM-YYYY").unix();

            var idCompany = this.idCompany || this.$route.params.idCompany;
            if (!idCompany) {
                this.$notify({
                    title: this.$t("error"),
                    message: this.$t("No se ha podido identificar la compañía"),
                    type: "error",
                    duration: 5000
                });
                return;
            }

            // La API comprueba que los TPV sean de la company y con permiso
            var params = {
                idCompany: idCompany,
                // Un envio por TPV o todos los TPV elegidos en el mismo envio
                tpvBatches: this.envioPorTpv,
                idTpvs: seleccion.idTpvs.join(','),
                idDays: seleccion.idDays.join(','),
                incluirQR: seleccion.incluirQR,
                jwt: this.$store.getters.jwt,
                exportDays: timestamp,
            };

            // Tamano de las llamadas a BC solo si se ha personalizado: 0 = todo de una vez
            if (this.tramoPersonalizado) {
                params.tamanoTramo = this.tramoTodo ? 0 : Math.max(50, parseInt(this.tamanoTramo, 10) || 100);
            }

            var config = {
                params: params,
                headers: {
                    Authorization: "Bearer " + this.$store.getters.jwt,
                }
            };

            // Nombres de TPV antes de recargar: despues ya no estaran en la lista
            var nombresTpv = {};
            this.daysToExport.forEach(v => { nombresTpv[String(v.idTpv)] = v.tpvName; });

            this.exportando = true;
            var cargando = Loading.service({ lock: true, text: this.$t("evExportandoTexto") });
            var resultado = null;

            try {
                var response = await this.axios.post(
                    this.$store.getters.apiUrl + "businesscentral/syncVentasToBusinessCentral",
                    {},
                    config
                );

                const data = (response && response.data) ? response.data : {};
                resultado = this.normalizarResultadoExportacion(data, nombresTpv);
                this.informeTanda = (data.result && data.result.informe) ? data.result.informe : null;
            } catch (error) {
                console.error("Error al exportar datos:", error);
                this.informeTanda = null;
                const sinRespuesta = !error || !error.response;
                resultado = {
                    estado: 'error',
                    mensaje: sinRespuesta ? this.$t("evSinRespuestaTanda") : this.$t("errorExportarDatos"),
                    totales: { exportados: 0, fallidos: 0, omitidos: 0 },
                    tpvs: [],
                };
            } finally {
                cargando.close();
                this.exportando = false;
            }

            // Los informes se piden aparte: aunque la peticion haya caducado en el navegador, el Excel esta
            await this.cargarInformesExportacion();

            // Primero el resultado; la lista y los contadores se recargan al cerrar el modal
            this.recargaPendiente = { seleccion: seleccion, estado: resultado.estado };
            this.resultadoExportacion = resultado;
            this.$modal.show('modal-resultado-exportacion');
        },

        /* Al cerrar el modal de resultado (X, boton o fondo). Se recarga aunque la tanda
           haya fallado: parte puede haber llegado igual */
        async recargarTrasResultado() {
            const pendiente = this.recargaPendiente;
            if (!pendiente) return;
            this.recargaPendiente = null;

            // La tabla se vacia y se quitan los filtros, igual que al pulsar "Cargar ventas"
            this.daysToExport = [];
            this.lineasPorVenta = {};
            this.consultaResuelta = false;
            this.errorCargaVentas = false;
            this.filasAbiertas = {};
            this.quitarFiltrosLocales();

            await this.cargarArbolExportacion();
            await this.fetchDataToExport(pendiente.seleccion);

            // Si queda algo con avisos, la tabla se abre directamente en ello
            if (pendiente.estado !== 'ok' && this.salesWithWarnings.length > 0) {
                this.quitarFiltrosLocales();
                this.vista = 'conAvisos';
            }
        },

        /* La API responde distinto en modo lotes (exitosos/fallidos/omitidos por
           TPV) y en modo normal (una sola tanda). Se deja todo como una lista por
           TPV con los numeros de documento. */
        normalizarResultadoExportacion(data, nombresTpv) {
            const result = data.result || {};
            const porTpv = {};

            const grupo = idTpv => {
                const clave = idTpv === null || idTpv === undefined ? 'todos' : String(idTpv);

                if (!porTpv[clave]) {
                    porTpv[clave] = {
                        clave: clave,
                        idTpv: clave === 'todos' ? null : clave,
                        nombre: clave === 'todos' ? this.$t('evTodosLosTpv') : (nombresTpv[clave] || ('TPV ' + clave)),
                        exportados: 0,
                        fallidos: [],
                        error: '',
                        omitidos: [],
                        info: '',
                    };
                }

                return porTpv[clave];
            };

            const documento = pedido => {
                if (!pedido) return '-';
                if (typeof pedido === 'string') return pedido;
                if (pedido.cab && pedido.cab.NumeroDocumento) return String(pedido.cab.NumeroDocumento);
                return String(pedido.documento || pedido.docNumber || '-');
            };

            // Motivo en palabras si la API lo trae; si no, el literal
            const omitido = venta => ({
                doc: documento(venta),
                motivo: venta.motivoUsuario || this.primerErrorValidacion(venta.errores),
            });

            const fallido = (pedido, usuario, literal) => {
                const doc = documento(pedido);
                return { doc: doc, motivo: (usuario && usuario[doc]) || (literal && literal[doc]) || '' };
            };

            const esLotes = Array.isArray(result.exitosos) || Array.isArray(result.fallidos);

            if (esLotes) {
                (result.exitosos || []).forEach(e => {
                    const g = grupo(e.tpvId);
                    g.exportados += parseInt(e.totalVentas, 10) || (e.documentosProcesados || []).length;
                    if (e.info) g.info = e.info;
                });

                (result.fallidos || []).forEach(f => {
                    const g = grupo(f.tpvId);
                    g.fallidos = g.fallidos.concat((f.documentosFallidos || [])
                        .map(p => fallido(p, f.erroresUsuarioPorDocumento, f.erroresPorDocumento)));
                    g.error = f.error || '';
                });

                (result.omitidos || []).forEach(o => {
                    const g = grupo(o.tpvId);
                    g.omitidos = g.omitidos.concat((o.ventasOmitidas || []).map(omitido));
                });
            } else {
                const g = grupo(null);
                const exitosos = result.pedidosExitosos || (data.response ? (result.jsonProcesado || []) : []);

                g.exportados = exitosos.length;
                g.fallidos = (result.pedidosFallidos || [])
                    .map(p => fallido(p, result.erroresUsuarioPorDocumento, result.erroresPorDocumento));
                g.omitidos = (result.ventasOmitidas || []).map(omitido);
                if (g.fallidos.length) g.error = data.message || '';
            }

            const tpvs = Object.keys(porTpv).map(k => porTpv[k])
                // Un TPV sin nada que contar (exportado 0 y sin errores) no aporta
                .filter(t => t.exportados > 0 || t.fallidos.length || t.omitidos.length);

            const totales = {
                exportados: tpvs.reduce((s, t) => s + t.exportados, 0),
                fallidos: tpvs.reduce((s, t) => s + t.fallidos.length, 0),
                omitidos: tpvs.reduce((s, t) => s + t.omitidos.length, 0),
            };

            let estado = 'error';
            if (totales.exportados > 0 && totales.fallidos === 0 && totales.omitidos === 0) estado = 'ok';
            else if (totales.exportados > 0) estado = 'parcial';

            return {
                estado: estado,
                mensaje: estado === 'ok' ? '' : (data.message || this.$t("errorExportarDatos")),
                totales: totales,
                tpvs: tpvs,
            };
        },

        /* Resultado inventado para ver el modal sin exportar. Solo con la flag de entorno. */
        async previsualizarRespuesta() {
            if (!this.previewRespuestaActivo) return;

            this.closeExportDataWarning();

            const nombre = idTpv => {
                const t = this.arbolExportacion.reduce((acc, d) => acc || (d.tpvs || []).find(x => x.idTpv === idTpv), null);
                return t ? t.nombre : ('TPV ' + idTpv);
            };
            const tpvA = this.seleccion.idTpvs[0] || 11847;
            const tpvB = this.seleccion.idTpvs[1] || 13726;

            this.resultadoExportacion = {
                estado: 'parcial',
                mensaje: this.$t('evMensajeEjemploParcial'),
                totales: { exportados: 140, fallidos: 5, omitidos: 3 },
                tpvs: [
                    {
                        clave: String(tpvA), idTpv: String(tpvA), nombre: nombre(tpvA),
                        exportados: 92, error: '',
                        fallidos: [
                            { doc: 'FS 0001/0000013', motivo: 'PAN SIN GLUTEN está dado de alta como otro tipo (producto o receta). Revisar su ficha antes de exportar.' },
                            { doc: 'FS 0001/0000021', motivo: 'PAN SIN GLUTEN está dado de alta como otro tipo (producto o receta). Revisar su ficha antes de exportar.' },
                            { doc: 'FA 0001/0000005', motivo: 'PAN SIN GLUTEN está dado de alta como otro tipo (producto o receta). Revisar su ficha antes de exportar.' },
                            { doc: 'FS 0001/0000035', motivo: 'ENSALADILLA APERITIVO* no tiene porcentaje de IVA.' },
                            { doc: 'FA 0001/0000008', motivo: 'La venta no lleva ningún pago registrado.' },
                            { doc: 'FR 0001/0000011', motivo: 'Cliente con NIF 12122121A no encontrado: la factura original no se ha exportado previamente.' },
                        ],
                        omitidos: [
                            { doc: 'FS 0001/0000034', motivo: 'ENSALADILLA APERITIVO* no tiene porcentaje de IVA.' },
                            { doc: 'FR 0001/0000009', motivo: 'El documento FS 0001/0000037 al que rectifica no se ha exportado todavía.' },
                        ],
                        info: '',
                    },
                    {
                        clave: String(tpvB), idTpv: String(tpvB), nombre: nombre(tpvB),
                        exportados: 48, error: '',
                        fallidos: [
                            { doc: 'FS 0008/0000036', motivo: 'ENSALADILLA APERITIVO* no tiene porcentaje de IVA.' },
                            { doc: 'FR 0008/0000011', motivo: 'Cliente con NIF 12122121A no encontrado: la factura original no se ha exportado previamente.' },
                        ],
                        omitidos: [{ doc: 'FS 0008/0000008', motivo: 'SAL FINA no tiene unidad de medida.' }],
                        info: '',
                    },
                ],
            };

            this.$modal.show('modal-resultado-exportacion');
            await this.cargarInformesExportacion();
            this.informeTanda = this.informesExportacion.length ? this.informesExportacion[0] : null;
        },

        /* Abre el modal solo con la lista de Excel, sin tanda nueva */
        async abrirInformes() {
            this.resultadoExportacion = null;
            this.informeTanda = null;
            this.$modal.show('modal-resultado-exportacion');
            await this.cargarInformesExportacion();
        },

        /* Informes Excel de exportacion que guarda la API para esta company */
        async cargarInformesExportacion() {
            const idCompany = this.idCompany || this.$route.params.idCompany;
            if (!idCompany) return;

            this.cargandoInformes = true;

            try {
                const response = await this.axios.get(
                    this.$store.getters.apiUrl + "businesscentral/listarInformesExportacion",
                    {
                        params: { idCompany: idCompany, jwt: this.$store.getters.jwt },
                        headers: { Authorization: "Bearer " + this.$store.getters.jwt },
                    }
                );

                const data = (response && response.data) ? response.data : {};
                this.informesExportacion = Array.isArray(data.result) ? data.result : [];
            } catch (error) {
                console.error("Error al cargar los informes de exportacion:", error);
                this.informesExportacion = [];
            } finally {
                this.cargandoInformes = false;
            }
        },

        descargarInformeExportacion(fichero) {
            const idCompany = this.idCompany || this.$route.params.idCompany;
            if (!idCompany || !fichero) return;

            window.open(
                this.$store.getters.apiUrl + "businesscentral/descargarInformeExportacion"
                + "?idCompany=" + idCompany
                + "&fichero=" + encodeURIComponent(fichero)
                + "&jwt=" + this.$store.getters.jwt
            );
        },

        esInformeDeLaTanda(informe) {
            return !!(this.informeTanda && informe && this.informeTanda.fichero === informe.fichero);
        },

        formatearFechaInforme(fecha) {
            return fecha ? moment(fecha, "YYYY-MM-DD HH:mm:ss").format("DD-MM-YYYY HH:mm") : "-";
        },

        /* Los errores del validador llegan anidados (cabecera y lineas): se
           enseña el primero y cuantos hay en total */
        primerErrorValidacion(errores) {
            const textos = [];
            const recorrer = valor => {
                if (typeof valor === 'string') textos.push(valor);
                else if (Array.isArray(valor)) valor.forEach(recorrer);
                else if (valor && typeof valor === 'object') Object.keys(valor).forEach(k => recorrer(valor[k]));
            };

            recorrer(errores);

            if (!textos.length) return '';
            return textos[0] + (textos.length > 1 ? ' (+' + (textos.length - 1) + ')' : '');
        },

        /* Como mucho LIMITE_RESULTADO filas por tarjeta en el modal de resultado */
        primeros(lista) {
            return (lista || []).slice(0, this.LIMITE_RESULTADO);
        },

        /* Documento del resultado que sigue en la lista: se puede saltar a el */
        ventaDelResultado(idTpv, doc) {
            if (!idTpv) {
                return this.daysToExport.find(v => String(v.docNumber || '').trim() === doc) || null;
            }

            return this.mapaDocumentos[idTpv + '|' + doc] || null;
        },

        irADocumentoDelResultado(idTpv, doc) {
            const venta = this.ventaDelResultado(idTpv, doc);
            if (!venta) return;

            this.$modal.hide('modal-resultado-exportacion');
            this.irADocumento(venta);
        },

        /* Modo desarrollador (config 133, en config_user). Solo habilita el botón
           de refrescar: no da acceso a nada que la 79 no diera ya. */
        async getDeveloperModePermission() {
            try {
                const response = await this.axios.get(
                    this.$store.getters.apiUrl + "general/getDeveloperModePermission",
                    {
                        params: {
                            jwt: this.$store.getters.jwt,
                            idConfig: 133,
                        },
                        headers: { Authorization: "Bearer " + this.$store.getters.jwt },
                    }
                );

                this.developerMode = !!(response.data && response.data.permission);
            } catch (error) {
                console.error("Error al obtener el permiso de modo desarrollador:", error);
                this.developerMode = false;
            }
        },

        /* Vuelve a pedir los días pendientes y los medios de pago, que es todo lo
           que carga el created(). Evita tener que salir del módulo y volver. */
        async refrescarDatos() {
            if (this.refrescando) return;

            this.refrescando = true;

            try {
                // Si aún no hay negocio resuelto, se rehace la carga desde el principio.
                if (!this.idBusiness) {
                    await this.getCompanies();
                    await this.cargarArbolExportacion(false);
                } else {
                    await this.cargarArbolExportacion();
                }

                await this.fetchDataToExport();

                this.$notify({
                    message: this.$t("datosActualizados"),
                    icon: "nc-icon nc-check-2",
                    timeout: 2000,
                    horizontalAlign: "right",
                    verticalAlign: "top",
                    type: "success",
                });
            } catch (error) {
                console.error("Error al refrescar los datos:", error);

                swal({
                    title: this.$t("atencion"),
                    text: this.$t("errorAlRefrescar"),
                    confirmButtonClass: "btn btn-info btn-fill",
                    type: "error",
                });
            } finally {
                this.refrescando = false;
            }
        },

        /* Comprueba un permiso contra general/getPermission, que respeta
           restrictions_info.tipo: las permisivas conceden con la fila presente. */
        async comprobarPermiso(idRestriccion, idBusiness) {
            // Si no hay negocio resuelto todavía (entrada por URL directa), la API
            // responde si lo tiene en alguno de los suyos. En cuanto se selecciona
            // negocio se vuelve a comprobar contra ese, en onBusinessChanged.
            try {
                const response = await this.axios.get(
                    this.$store.getters.apiUrl + "general/getPermission",
                    {
                        params: {
                            jwt: this.$store.getters.jwt,
                            idBusiness: idBusiness || "",
                            idRestriccion: idRestriccion,
                        },
                        headers: { Authorization: "Bearer " + this.$store.getters.jwt },
                    }
                );

                return !!(response.data && response.data.result && response.data.result.permitido);
            } catch (error) {
                console.error("Error comprobando el permiso " + idRestriccion + ":", error);
                // Si no se puede comprobar, no se concede: esto es control de acceso.
                return false;
            }
        },

        /* La 79 da acceso al módulo. La 80, además, permite editar.
           Se llama una vez resuelto el idBusiness, no antes: con el negocio sin
           resolver la comprobación denegaba y expulsaba a todo el mundo. */
        async comprobarAccesoExportacion() {
            const puedeEntrar = await this.comprobarPermiso(79, this.idBusiness || null);

            if (!puedeEntrar) {
                swal({
                    title: this.$t("atencion"),
                    text: this.$t("sinPermisoExportacionBC"),
                    confirmButtonClass: "btn btn-info btn-fill",
                    type: "warning",
                });

                this.$router.push({ name: "businesscentral" });
                return false;
            }

            this.permisoEdicionBC = await this.comprobarPermiso(80, this.idBusiness || null);

            return true;
        },

        /* ---------- Ayudas de presentación de la tabla ---------- */

        formatAmount(valor) {
            const numero = parseFloat(valor);
            return (isNaN(numero) ? 0 : numero).toFixed(2) + ' €';
        },

        documentTypeLabel(tipo) {
            if (tipo === 'R') return this.$t('rectificativa');
            if (tipo === 'F') return this.$t('factura');
            return this.$t('ticket');
        },

        /* El importe de una venta con vale o de una avanzada no sale de
           headers.amount, y conviene que se vea de dónde sale. */
        amountSourceLabel(venta) {
            const origenes = {
                vales: this.$t('origenImporteVales'),
                valesDocumentoOriginal: this.$t('origenImporteValesOriginal'),
                cobrosavanzados: this.$t('origenImporteCobrosAvanzados'),
            };

            return origenes[venta.origenImporte] || '';
        },

        /* Con un solo medio de pago el desglose repite lo que ya dice la fila:
           solo se enseña cuando hay reparto de verdad. */
        showPaymentBreakdown(venta) {
            return (venta.desglose || []).length > 1;
        },

        paymentBreakdownSummary(venta) {
            return (venta.desglose || []).map(pago => pago.nombre).join(' + ');
        },

        /* Cualquier aviso, incluidos los informativos */
        hasAvisos(venta) {
            return Array.isArray(venta.avisos) && venta.avisos.length > 0;
        },

        /* Solo los que cuentan como aviso: los de nivel 'info' no */
        /* Avisos de verdad: ni informativos ni la marca de excluida */
        hasWarnings(venta) {
            return this.hasAvisos(venta)
                && venta.avisos.some(a => a.nivel !== 'info' && a.codigo !== 'avisoExcluidaManual');
        },

        esVentaLista(venta) {
            return venta.estadoExportacion !== 4 && this.nivelVenta(venta) !== 'bloqueante';
        },

        hasDetail(venta) {
            const desglose = venta.desglose || [];
            const vales = venta.vales || [];

            return desglose.length > 1 || vales.length > 0 || this.hasAvisos(venta);
        },

        isRowOpen(venta) {
            return !!this.filasAbiertas[venta.id];
        },

        toggleDetail(venta) {
            this.$set(this.filasAbiertas, venta.id, !this.filasAbiertas[venta.id]);
        },

        /* ---------- Niveles de aviso ---------- */

        /* El peor nivel de la venta: 'bloqueante', 'revisar', 'info' o '' si no tiene */
        nivelVenta(venta) {
            if (!this.hasAvisos(venta)) return '';
            if (venta.avisos.some(a => a.nivel === 'bloqueante')) return 'bloqueante';
            if (venta.avisos.some(a => a.nivel !== 'info')) return 'revisar';
            return 'info';
        },

        nivelLabel(nivel) {
            if (nivel === 'bloqueante') return this.$t('evNivelBloqueante');
            if (nivel === 'info') return this.$t('evNivelInfo');
            return this.$t('evNivelRevisar');
        },

        nivelIcono(nivel) {
            if (nivel === 'bloqueante') return 'fa-times-circle';
            if (nivel === 'info') return 'fa-info-circle';
            return 'fa-exclamation-triangle';
        },

        nivelTexto(nivel) {
            if (nivel === 'bloqueante') return this.$t('evNivelBloqueanteTexto');
            if (nivel === 'info') return this.$t('evNivelInfoTexto');
            return this.$t('evNivelRevisarTexto');
        },

        /* Pie del bloque de avisos; una excluida a mano tiene su propio texto */
        textoEstadoVenta(venta) {
            if (venta.estadoExportacion === 4) return this.$t('evExcluidaTexto');
            return this.nivelTexto(this.nivelVenta(venta));
        },

        filaClase(venta) {
            const nivel = this.nivelVenta(venta);

            return {
                'fila-bloqueante': nivel === 'bloqueante',
                'fila-revisar': nivel === 'revisar',
            };
        },

        /* v-tooltip pinta HTML por defecto: se escapa el texto y se separa con <br> */
        tooltipAvisos(venta) {
            const escapar = texto => String(texto)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');

            return (venta.avisos || [])
                .map(a => '• ' + escapar(this.$t(a.codigo) + (a.detalle ? ': ' + a.detalle : '')))
                .join('<br>');
        },

        /* El unico aviso que se arregla desde esta pantalla: asignar cliente */
        tieneAvisoEditable(venta) {
            return (venta.avisos || []).some(a => a.codigo === 'avisoMedioPagoSinCliente' || a.codigo === 'avisoCuentaCasaClienteGenerico');
        },

        /* ---------- Filtros locales ---------- */

        /* Al filtrar desde el resumen se baja hasta la barra: la tabla queda lejos
           y parecia que no habia pasado nada */
        toggleFiltroAviso(codigo) {
            this.filtroAviso = this.filtroAviso === codigo ? '' : codigo;
            if (this.filtroAviso) this.$nextTick(() => this.scrollABarra());
        },

        scrollABarra() {
            const el = this.$refs.barraFiltros;
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },

        toggleFiltro(campo, valor) {
            this[campo] = this[campo] === valor ? '' : valor;
        },

        /* Filtros de tarjetas, aviso y buscador. `excepto` deja fuera uno de ellos
           ('tpv', 'tipoDoc', 'aviso', 'medio') para que su tarjeta siga mostrando
           todas sus filas y se pueda cambiar o quitar el filtro desde ella */
        filtrarLocal(ventas, excepto) {
            if (this.filtroTpv && excepto !== 'tpv') {
                ventas = ventas.filter(v => String(v.idTpv) === this.filtroTpv);
            }

            if (this.filtroTipoDoc && excepto !== 'tipoDoc') {
                ventas = ventas.filter(v => (v.tipoDocumento || 'T') === this.filtroTipoDoc);
            }

            if (this.filtroAviso && excepto !== 'aviso') {
                ventas = ventas.filter(v => (v.avisos || []).some(a => a.codigo === this.filtroAviso));
            }

            if (this.filtroMedioResumen && excepto !== 'medio') {
                ventas = ventas.filter(v => this.clavesMedioVenta(v).indexOf(this.filtroMedioResumen) !== -1);
            }

            const texto = this.normalizarTexto(this.busqueda);
            if (texto) {
                ventas = ventas.filter(v => this.textoBusquedaVenta(v).indexOf(texto) !== -1);
            }

            return ventas;
        },

        quitarFiltrosLocales() {
            this.busqueda = '';
            this.vista = 'todas';
            this.filtroTpv = '';
            this.filtroTipoDoc = '';
            this.filtroAviso = '';
            this.filtroMedioResumen = '';
            this.modoAvanzadas = 'todas';
        },

        /* Mismas claves que summaryByPaymentMethod, para poder filtrar por fila */
        clavesMedioVenta(venta) {
            const pagos = (venta.desglose && venta.desglose.length > 0)
                ? venta.desglose
                : [{ nombre: venta.medioPago || this.$t('sinDatos'), codigoBC: venta.codigoBC || '' }];

            return pagos.map(pago => (pago.codigoBC || '') + '|' + (pago.nombre || ''));
        },

        /* ---------- Orden del listado ---------- */

        /* Ascendente -> descendente -> sin orden */
        ordenarPor(campo) {
            if (this.orden.campo !== campo) {
                this.orden = { campo: campo, dir: 1 };
            } else if (this.orden.dir === 1) {
                this.orden = { campo: campo, dir: -1 };
            } else {
                this.orden = { campo: '', dir: 1 };
            }
        },

        iconoOrden(campo) {
            if (this.orden.campo !== campo) return 'fa-sort';
            return this.orden.dir === 1 ? 'fa-sort-up' : 'fa-sort-down';
        },

        valorOrden(venta, campo) {
            switch (campo) {
                case 'fecha': return Number(venta.date) || 0;
                case 'tpv': return venta.tpvName || '';
                case 'documento': return venta.docNumber || venta.ref_docNumber || '';
                case 'cliente': return venta.cliente ? (venta.cliente.nombreCompleto || '') : '';
                case 'formaPago': return venta.formaPago || venta.tipoFormaPago || '';
                case 'medioPago': return venta.paidType === 'A' ? this.$t('variasFormas') : (venta.medioPago || '');
                case 'importe': return parseFloat(venta.importe) || 0;
                case 'comensales': return parseInt(venta.num_comensales, 10) || 0;
                default: return '';
            }
        },

        /* Copia ordenada. Los vacios van siempre al final y, a igualdad, se mantiene
           el orden de llegada (sort no es estable en todos los navegadores) */
        ordenarVentas(ventas) {
            const campo = this.orden.campo;
            const dir = this.orden.dir;

            return ventas
                .map((venta, indice) => ({ venta, indice, valor: this.valorOrden(venta, campo) }))
                .sort((a, b) => {
                    const vacioA = a.valor === '';
                    const vacioB = b.valor === '';
                    if (vacioA !== vacioB) return vacioA ? 1 : -1;

                    let cmp = 0;
                    if (typeof a.valor === 'number' && typeof b.valor === 'number') {
                        cmp = a.valor - b.valor;
                    } else {
                        cmp = String(a.valor).localeCompare(String(b.valor), 'es', { numeric: true, sensitivity: 'base' });
                    }

                    return cmp !== 0 ? cmp * dir : a.indice - b.indice;
                })
                .map(item => item.venta);
        },

        normalizarTexto(texto) {
            return String(texto || '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/[̀-ͯ]/g, '')
                .trim();
        },

        /* Documento, documento rectificado y datos del cliente */
        textoBusquedaVenta(venta) {
            const cliente = venta.cliente || {};

            return this.normalizarTexto([
                venta.docNumber,
                venta.documentoRectificado,
                cliente.nombreCompleto,
                cliente.documento,
                cliente.codigo,
            ].join(' '));
        },

        /* ---------- Paginacion ---------- */

        /* Cambio de pagina desde los controles: se sube al principio de la tabla,
           que si no se quedaba uno al final de la pagina nueva */
        irAPagina(numero) {
            const destino = Math.min(Math.max(1, numero), this.totalPaginas);
            if (destino === this.pagina) return;

            this.pagina = destino;
            this.$nextTick(() => this.scrollABarra());
        },

        /* ---------- Enlace rectificativa <-> original ---------- */

        originalEnLista(venta) {
            const ref = String(venta.documentoRectificado || '').trim();
            if (!ref) return null;

            const original = this.mapaDocumentos[venta.idTpv + '|' + ref];
            return original && original.id !== venta.id ? original : null;
        },

        rectificativasDe(venta) {
            const doc = String(venta.docNumber || '').trim();
            if (!doc) return SIN_RECTIFICATIVAS;

            return this.mapaRectificativas[venta.idTpv + '|' + doc] || SIN_RECTIFICATIVAS;
        },

        /* Salta al documento aunque este en otra pagina u oculto por un filtro:
           quita los filtros que lo esconden, cambia de pagina, hace scroll y lo
           resalta un momento. */
        irADocumento(destino) {
            if (!destino) return;

            const buscar = () => this.ventasVista.findIndex(v => v.id === destino.id);

            if (buscar() === -1) this.quitarFiltrosLocales();

            // Se espera a que el watcher de filtros haya devuelto la pagina a 1
            this.$nextTick(() => {
                const indice = buscar();
                if (indice === -1) return;

                this.pagina = Math.floor(indice / this.porPagina) + 1;

                this.$nextTick(() => {
                    const el = document.getElementById('ev-venta-' + destino.id);
                    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });

                    this.resaltadoId = destino.id;
                    clearTimeout(this.temporizadorResaltado);
                    this.temporizadorResaltado = setTimeout(() => { this.resaltadoId = null; }, 2500);
                });
            });
        },

        notEditableReason(venta) {
            if (!venta || venta.editable) return '';
            return venta.motivoNoEditable ? this.$t(venta.motivoNoEditable) : this.$t('ventaNoEditable');
        },

        clientLabel(cliente) {
            let etiqueta = cliente.nombreCompleto || '';

            if (cliente.documento) etiqueta += ' · ' + cliente.documento;
            if (cliente.codigo) etiqueta += ' · ' + cliente.codigo;
            if (!cliente.activo) etiqueta += ' · ' + this.$t('dadoDeBaja');

            return etiqueta;
        },

        /* ---------- Edición del medio de pago ---------- */

        async editarFormaPago(venta) {
            // La misma regla que aplica la API. Aquí solo evita abrir el modal
            // para nada: quien decide de verdad es updateFormaPagoByTpv.
            if (!venta.editable) {
                swal({
                    title: this.$t('atencion'),
                    text: this.notEditableReason(venta),
                    confirmButtonClass: 'btn btn-info btn-fill',
                    type: 'warning'
                });
                return;
            }

            this.ventaSeleccionada = Object.assign({}, venta);
            this.medioPagoSeleccionado = parseInt(venta.idFormaPago, 10) || 0;
            this.medioPagoOriginal = this.medioPagoSeleccionado;
            this.clienteSeleccionado = venta.idClient > 0 ? venta.idClient : null;
            this.clienteOriginal = this.clienteSeleccionado;

            // El cliente que ya tiene la venta se precarga para que el desplegable
            // lo muestre sin tener que buscarlo otra vez.
            this.clientesEncontrados = venta.cliente
                ? [{
                    id: venta.cliente.id,
                    nombreCompleto: venta.cliente.nombreCompleto,
                    documento: venta.cliente.documento,
                    codigo: venta.cliente.codigo,
                    esSocio: venta.cliente.esSocio,
                    activo: venta.cliente.activo,
                    sinCodigo: !venta.cliente.codigo,
                }]
                : [];

            const config = {
                params: {
                    jwt: this.$store.getters.jwt,
                    idBusiness: this.negocioDeTpv(venta.idTpv),
                    // Las formas de pago son por TPV: hay que pedir las del TPV de
                    // esta venta, no las del TPV servidor del negocio.
                    idTpv: venta.idTpv,
                },
                headers: {
                    Authorization: "Bearer " + this.$store.getters.jwt
                },
            };

            try {
                const response = await this.axios.get(
                    this.$store.getters.apiUrl + "businesscentral/getFormasPagoByTpv",
                    config
                );

                this.formasPagoPorTipo = response.data || { E: [], T: [], C: [] };
                this.openModalFormaPagoVisible();
            } catch (error) {
                console.error('Error al obtener formas de pago:', error);
                swal({
                    title: this.$t('error'),
                    text: this.$t('errorCargandoFormasPago'),
                    confirmButtonClass: 'btn btn-danger btn-fill',
                    type: 'error'
                });
            }
        },

        async guardarFormaPagoSeleccionada() {
            if (!this.isFormaPagoChanged) {
                swal({
                    title: this.$t('atencion'),
                    text: this.$t('debeCambiarFormaPagoParaGuardar'),
                    confirmButtonClass: 'btn btn-warning btn-fill',
                    type: 'warning'
                });
                return;
            }

            if (this.paymentMethodRequiresClient && !this.clienteSeleccionado) {
                swal({
                    title: this.$t('atencion'),
                    text: this.$t('medioPagoRequiereClienteTexto'),
                    confirmButtonClass: 'btn btn-warning btn-fill',
                    type: 'warning'
                });
                return;
            }

            const config = {
                params: {
                    idBusiness: this.negocioDeTpv(this.ventaSeleccionada ? this.ventaSeleccionada.idTpv : null),
                    jwt: this.$store.getters.jwt,
                },
                headers: {
                    Authorization: "Bearer " + this.$store.getters.jwt,
                    'Content-Type': 'application/json'
                }
            };

            // El tipo de pago se deduce del medio elegido; los importes no viajan.
            const payload = {
                idVenta: this.ventaSeleccionada.id,
                idFormaPago: this.medioPagoSeleccionado,
                paidType: this.selectedPaymentMethod ? this.selectedPaymentMethod.tipo : 'E',
            };

            if (this.paymentMethodRequiresClient || this.clienteSeleccionado !== this.clienteOriginal) {
                payload.idClient = this.clienteSeleccionado;
            }

            this.guardandoFormaPago = true;

            try {
                const response = await this.axios.post(
                    this.$store.getters.apiUrl + "businesscentral/updateFormaPagoByTpv",
                    payload,
                    config
                );

                const datos = response.data || {};

                if (datos.response) {
                    this.$notify({
                        message: this.$t('updatePagoCorrecto'),
                        icon: 'nc-icon nc-check-2',
                        timeout: 2000,
                        horizontalAlign: 'right',
                        verticalAlign: 'top',
                        type: 'success'
                    });

                    this.closeModalFormaPagoVisible();
                    await this.fetchDataToExport(this.seleccionCargada);
                } else {
                    swal({
                        title: this.$t('atencion'),
                        text: datos.motivo ? this.$t(datos.motivo) : (datos.message || this.$t('noSePudoActualizarFormaPago')),
                        confirmButtonClass: 'btn btn-info btn-fill',
                        type: 'error'
                    });
                }
            } catch (error) {
                console.error("Error al actualizar forma de pago:", error);
                swal({
                    title: this.$t('error'),
                    text: this.$t('errorAlGuardarFormaPago'),
                    confirmButtonClass: 'btn btn-danger btn-fill',
                    type: 'error'
                });
            } finally {
                this.guardandoFormaPago = false;
            }
        },

        /* Busca clientes del mismo grupo empresarial para los medios de pago que
           exigen uno. Va contra la API de exportación, que devuelve además el
           código de socio: sin él la exportación acaba preguntándole a Salesforce. */
        async searchClients(query) {
            if (!query || query.trim().length < 2) {
                this.clientesEncontrados = [];
                return;
            }

            this.buscandoClientes = true;

            try {
                const response = await this.axios.get(
                    this.$store.getters.apiUrl + "businesscentral/searchClientsForSale",
                    {
                        params: {
                            jwt: this.$store.getters.jwt,
                            idBusiness: this.negocioDeTpv(this.ventaSeleccionada ? this.ventaSeleccionada.idTpv : null),
                            idTpv: this.ventaSeleccionada ? this.ventaSeleccionada.idTpv : null,
                            query: query.trim(),
                        },
                        headers: { Authorization: "Bearer " + this.$store.getters.jwt },
                    }
                );

                this.clientesEncontrados = (response.data && response.data.result) ? response.data.result : [];
            } catch (error) {
                console.error("Error buscando clientes:", error);
                this.clientesEncontrados = [];
            } finally {
                this.buscandoClientes = false;
            }
        },
    },
}
</script>

<style scoped>
/* Refrescar dentro del aviso de "no hay días para exportar": solo el icono,
   en blanco sobre el verde del alert y centrado bajo los textos. */
.btn-refrescar-icono {
    display: block;
    margin: 14px auto 2px;
    padding: 0;
    width: 38px;
    height: 38px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: #fff;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    opacity: 0.85;
    transition: opacity 0.2s ease, background-color 0.2s ease;
}

.btn-refrescar-icono:hover:not(:disabled) {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.18);
}

.btn-refrescar-icono:disabled {
    opacity: 0.5;
    cursor: default;
}

.button-container {
    display: flex;
    justify-content: center;
    align-items: center;
}

.floating-btn i {
    margin-right: 10px;
}

.floating-btn-container {
    position: fixed;
    bottom: 1rem;
    right: 2rem;
    z-index: 1000;
}

.floating-btn {
    background-color: #101255;
    display: inline-flex;
    align-items: center;
    color: white;
    border: none;
    padding: 15px 30px;
    font-size: 16px;
    font-weight: bold;
    border-radius: 5px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    cursor: pointer;
}

.floating-btn:hover {
    background-color: #0a0f3c;
    transform: scale(1.05);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.floating-btn:active {
    transform: scale(1);
    box-shadow: none;
}

/* Mientras se exporta */
.floating-btn:disabled {
    opacity: 0.75;
    cursor: wait;
    transform: none;
}

.floating-btn-raised {
    bottom: 4.5rem !important;
}

.close-btn {
    position: absolute;
    top: 10px;
    right: 15px;
    font-size: 24px;
    font-weight: bold;
    color: #aaa;
    cursor: pointer;
    z-index: 1000;
    transition: color 0.2s;
}

.close-btn:hover {
    color: #000;
}

.fila-totales {
    background-color: #FFEEBA !important;
    font-weight: bold;
}

.fila-totales td {
    font-weight: bold;
    color: #333;
}

.fila-totales .text-left {
    text-align: left !important;
}

.modal-detalles-forma-pago {
    width: 100%;
    margin: 0 auto;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.modal-header.wrapperTitle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    background-color: #f5f6fa;
    border-bottom: 1px solid #ddd;
}

.modal-header h4 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #101255;
}

.close-btn {
    font-size: 1.4rem;
    cursor: pointer;
    color: #999;
    transition: color 0.2s;
}

.close-btn:hover {
    color: #333;
}

.bottom-spacer {
    width: 100%;
    height: 0;
    transition: height 0.35s cubic-bezier(.4, 0, .2, 1);
    pointer-events: none;
}

.bottom-spacer--visible {
    height: 25px;
}

/* === Modo de Exportación (normal/lotes) === */
.export-mode-card {
    background: #ffffff;
    border: 1px solid #e8ecf0;
    border-radius: 14px;
    padding: 16px 18px;
    margin-bottom: 18px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.export-mode-card h5 {
    font-size: 1rem;
}

/* Selector de TPV dentro de la opcion "un TPV" */
.export-option-cuerpo {
    min-width: 0;
}

/* Ancho limitado: sin esto crecia hasta el borde de la tarjeta. Si el nombre
   del TPV no cabe, se corta con puntos suspensivos. */
.export-option-select {
    margin-top: 4px;
    width: 100%;
    max-width: 240px;
    height: 30px;
    padding: 2px 8px;
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* === Cifras del modal previo y del resultado === */
.ev-previo {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
    margin: 18px 0 8px;
}

.ev-previo-cifra {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 10px 12px;
    border: 1px solid #e4e7ee;
    border-radius: 10px;
    background: #f6f7fb;
    color: #5b6183;
}

.ev-previo-cifra span {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.ev-previo-cifra strong {
    font-size: 1.5rem;
    line-height: 1.2;
    color: #101255;
}

.ev-previo-cifra small {
    font-size: 0.78rem;
}

.ev-previo-verde {
    background: #eef8ef;
    border-color: #cfe8d1;
}

.ev-previo-verde strong {
    color: #2e7d32;
}

.ev-previo-rojo {
    background: #fff5f5;
    border-color: #f3c9c7;
}

.ev-previo-rojo strong {
    color: #c62828;
}

.ev-previo-ambar {
    background: #fffaf0;
    border-color: #f5dfb6;
}

.ev-previo-ambar strong {
    color: #8d6e00;
}

/* === Modal de resultado === */
.ev-res-ok {
    color: #2e7d32;
}

.ev-res-parcial {
    color: #e0a12a;
}

.ev-res-error {
    color: #c62828;
}

/* Estado de exportacion en la columna de documento */
.chip-estado {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 7px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    vertical-align: middle;
}

.chip-estado-2 {
    background: #fde8e8;
    color: #b42318;
}

.chip-estado-3 {
    background: #fff4d6;
    color: #8a5a00;
}

.chip-estado-4 {
    background: #e9ecf3;
    color: #4b5563;
}

.ev-asociados-texto {
    margin: 0 0 10px;
    font-size: 13px;
    color: #344054;
}

.ev-asociados-lista {
    list-style: none;
    margin: 0 0 12px;
    padding: 0;
    max-height: 220px;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
}

.ev-asociados-lista li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 10px;
    font-size: 13px;
    border-bottom: 1px solid #f0f1f4;
}

.ev-asociados-lista li:last-child {
    border-bottom: 0;
}

.ev-asociados-fecha {
    color: #667085;
    font-size: 12px;
}

.ev-asociados-ref {
    color: #667085;
    font-size: 12px;
    margin-left: auto;
}

.accion-excluir {
    color: #b42318;
}

/* Fila con una exclusion/inclusion en vuelo: sin clics hasta que responda */
.acciones-btns.acciones-ocupadas {
    opacity: 0.55;
    pointer-events: none;
}

.accion-incluir {
    color: #1d6f42;
}

.ev-chip-rechazados.ev-chip-activo {
    background: #b42318;
    color: #fff;
}

.ev-chip-omitidos.ev-chip-activo {
    background: #8a5a00;
    color: #fff;
}

.ev-chip-excluidos.ev-chip-activo {
    background: #4b5563;
    color: #fff;
}

/* Tarjeta informativa del modal previo (que se exporta) */
.dv-seccion-info {
    --dv-color: #4a7dbf;
    --dv-fondo: #f3f7fc;
}

.ev-alcance {
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: 12px;
    row-gap: 6px;
    font-size: 0.88rem;
}

.ev-alcance-fila {
    display: contents;
}

.ev-alcance-etiqueta {
    font-weight: 600;
    color: #3c4166;
    white-space: nowrap;
}

.ev-alcance-valor {
    color: #3c4166;
}

/* Los TPV como fichas; con muchos, la caja hace scroll en vez de crecer */
.ev-alcance-tpvs {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-height: 88px;
    overflow-y: auto;
    padding-right: 4px;
}

.ev-alcance-chip {
    display: inline-block;
    padding: 1px 8px;
    border: 1px solid #c9d6ea;
    border-radius: 10px;
    background: #fff;
    color: #2f4f7f;
    font-size: 0.8rem;
    line-height: 18px;
    white-space: nowrap;
}

/* Selector de envio en el modal previo */
.ev-modo-envio {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 8px;
    margin-top: 12px;
}

.ev-modo-envio-titulo {
    flex-basis: 100%;
    font-size: 0.88rem;
    font-weight: 600;
    color: #3c4166;
}

.ev-btn-modal-preview {
    margin-right: auto;
}

.ev-modo-envio-opcion {
    flex: 1 1 200px;
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin: 0;
    padding: 8px 10px;
    border: 1px solid #e3e6ee;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    font-weight: normal;
}

.ev-modo-envio-opcion input {
    margin-top: 3px;
}

/* Nota y personalizacion del tamano de las llamadas a BC */
.ev-tramo-nota {
    flex-basis: 100%;
    margin: 4px 0 0;
    font-size: 0.78rem;
    color: #5b6183;
}

.ev-tramo-check {
    flex-basis: 100%;
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 0.82rem;
    font-weight: normal;
    color: #3c4166;
    cursor: pointer;
}

.ev-tramo-opciones {
    flex-basis: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    padding-left: 22px;
}

.ev-tramo-radio {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 0.82rem;
    font-weight: normal;
    color: #3c4166;
    cursor: pointer;
}

.ev-tramo-input {
    width: 90px;
    height: 28px;
    padding: 2px 6px;
    font-size: 0.82rem;
}

/* Titulo y descripcion de cada opcion, al tamano del texto de la tarjeta */
.ev-modo-envio-opcion strong {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #101255;
}

.ev-modo-envio-opcion small {
    display: block;
    color: #6c757d;
    font-size: 0.78rem;
}

.ev-modo-envio-activa {
    border-color: #101255;
    background: #eceef8;
}

.ev-res-informes {
    margin-top: 14px;
    padding: 12px 14px;
    border: 1px solid #e3e6ee;
    border-radius: 8px;
    background: #f8f9fc;
}

.ev-res-informes-cabecera {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
}

.ev-res-informes-cabecera .fa-file-excel {
    color: #1d6f42;
}

.ev-res-informes-refrescar {
    font-size: 12px;
    color: #6c757d;
    cursor: pointer;
}

.ev-res-informes-vacio {
    font-size: 13px;
    margin: 0;
}

.ev-res-informes-lista {
    list-style: none;
    margin: 0;
    padding: 0;
}

.ev-res-informes-lista li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    border-top: 1px solid #e9ecf3;
    font-size: 13px;
}

.ev-res-informes-lista li:first-child {
    border-top: 0;
}

.ev-res-informe-actual {
    font-weight: 600;
}

.ev-res-informe-fecha {
    min-width: 120px;
}

.ev-res-informe-conteo {
    font-size: 12px;
}

.ev-res-rechazadas .ev-res-doc {
    color: #b42318;
}

/* Con muchos TPV, las tarjetas hacen scroll; los informes de abajo no se mueven */
.ev-res-tpvs-scroll {
    max-height: 340px;
    overflow-y: auto;
    padding-right: 4px;
}

.ev-res-mas {
    font-size: 12px;
    font-style: italic;
    color: #6c757d;
}

.ev-res-informe-descargar {
    margin-left: auto;
    border: 1px solid #1d6f42;
    background: #fff;
    color: #1d6f42;
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 12px;
    cursor: pointer;
}

.ev-res-informe-descargar:hover {
    background: #1d6f42;
    color: #fff;
}

.ev-res-mensaje {
    margin: 0;
    color: #5b6183;
}

.ev-res-tpv {
    margin-top: 12px;
    padding: 10px 12px;
    border: 1px solid #e4e7ee;
    border-radius: 8px;
}

.ev-res-tpv-cabecera {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.ev-res-chip {
    border-radius: 10px;
    padding: 0 8px;
    font-size: 0.74rem;
    font-weight: 600;
}

.ev-res-chip-ok {
    background: #e8f5e9;
    color: #2e7d32;
}

.ev-res-chip-error {
    background: #fdecea;
    color: #b3261e;
}

.ev-res-chip-ambar {
    background: #fff1d6;
    color: #8d6e00;
}

.ev-res-bloque {
    margin-top: 8px;
}

.ev-res-error-texto {
    color: #b3261e;
    margin-bottom: 6px;
    word-break: break-word;
}

.ev-res-docs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.ev-res-doc {
    font-weight: 600;
    font-size: 0.8rem;
    color: #3c4166;
}

.ev-res-omitidos {
    margin: 8px 0 0;
    padding-left: 18px;
    font-size: 0.8rem;
    max-height: 220px;
    overflow-y: auto;
}

.ev-res-omitidos li {
    margin: 2px 0;
}

.ev-res-omitidos .text-muted {
    margin-left: 6px;
}

/* Las opciones en fila; por debajo de ~560px de ancho, en columna */
.export-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 12px;
}

.export-icon-wrap {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #0d6efd, #6366f1);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.85rem;
    flex-shrink: 0;
    margin-right: 14px;
    box-shadow: 0 4px 10px rgba(99, 102, 241, 0.3);
}

/* === Opciones seleccionables === */
.export-option {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border: 2px solid #e8ecf0;
    border-radius: 12px;
    cursor: pointer;
    background: #f9fafb;
    margin-bottom: 0;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
    user-select: none;
}


.export-option:hover {
    border-color: #a5b4fc;
    background: #f5f3ff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
}

.export-option-active {
    border-color: #0d6efd;
    background: linear-gradient(135deg, #f0f4ff, #f5f3ff);
    box-shadow: 0 4px 16px rgba(13, 110, 253, 0.12);
    transform: translateY(-1px);
}

/* Ocultar el radio nativo */
.export-option-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
}

/* === Icono de la opción === */
.export-option-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #e8ecf0;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
    transition: background 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
}

.export-option-icon-active {
    background: linear-gradient(135deg, #0d6efd, #6366f1);
    color: white;
    box-shadow: 0 4px 10px rgba(13, 110, 253, 0.3);
}

/* === Textos === */
.export-option-label {
    font-weight: 600;
    font-size: 0.9rem;
    color: #1e2535;
    margin-bottom: 2px;
    transition: color 0.2s ease;
}

.export-option-desc {
    color: #9ca3af;
    line-height: 1.4;
    font-size: 0.78rem;
}

/* === Check animado === */
.export-option-check {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0d6efd, #6366f1);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6rem;
    flex-shrink: 0;
    opacity: 0;
    transform: scale(0.5);
    transition: opacity 0.2s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.export-option-check-visible {
    opacity: 1;
    transform: scale(1);
}

/* === Resumen previo a la exportación === */
.resumen-exportacion {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
    margin-bottom: 18px;
}

.resumen-bloque {
    background: #fff;
    border: 1px solid #e4e7ee;
    border-radius: 10px;
    padding: 14px 16px;
    box-shadow: 0 1px 3px rgba(16, 18, 85, 0.05);
}

.resumen-bloque h6 {
    margin: 0 0 10px 0;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #101255;
}

.resumen-bloque h6 i {
    margin-right: 6px;
    color: #7a82a8;
}

.tabla-resumen {
    width: 100%;
    margin: 0;
    font-size: 0.86rem;
}

.tabla-resumen td {
    padding: 5px 0;
    border-bottom: 1px dashed #eef0f5;
    vertical-align: middle;
}

.tabla-resumen tr:last-child td {
    border-bottom: none;
}

/* El nombre es lo único que se estira; el resto son columnas de ancho mínimo,
   así los importes quedan en la misma vertical aunque los nombres varíen. */
.resumen-nombre {
    width: 100%;
    max-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.resumen-codigo {
    width: 1%;
    white-space: nowrap;
    padding-left: 8px !important;
}

.resumen-docs {
    width: 1%;
    white-space: nowrap;
    text-align: center;
    padding-left: 10px !important;
}

.resumen-docs .contador {
    display: inline-block;
    min-width: 26px;
    background: #eef0f7;
    border-radius: 20px;
    padding: 1px 8px;
    font-size: 0.74rem;
    color: #5b6183;
}

.resumen-importe {
    width: 1%;
    white-space: nowrap;
    text-align: right;
    padding-left: 12px !important;
    font-weight: 700;
    color: #101255;
}

/* Código con el que viaja el medio de pago a Business Central */
.chip-bc {
    background: #eef2ff;
    color: #3b4a9c;
    border: 1px solid #d8ddf7;
    border-radius: 4px;
    padding: 0 5px;
    font-size: 0.72rem;
}

/* === Tabla unificada === */
.tabla-exportacion td {
    vertical-align: middle;
}

/* Cabeceras ordenables */
.tabla-exportacion .th-ordenable {
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
}

.tabla-exportacion .th-orden-icono {
    margin-left: 4px;
    opacity: 0.45;
    font-size: 0.75em;
}

.tabla-exportacion .th-ordenada .th-orden-icono {
    opacity: 1;
}

.tabla-exportacion .col-cliente {
    min-width: 240px;
}

.tabla-exportacion .col-comensales {
    width: 62px;
    white-space: nowrap;
}

.tabla-exportacion small {
    font-size: 0.74rem;
}

/* Fila coloreada por el peor aviso: borde en la primera celda y fondo suave */
.fila-bloqueante td {
    background-color: #fdeeee;
}

.fila-bloqueante td:first-child {
    box-shadow: inset 4px 0 0 #d9534f;
}

.fila-revisar td {
    background-color: #fff8e8;
}

.fila-revisar td:first-child {
    box-shadow: inset 4px 0 0 #e0a12a;
}

.col-desplegar {
    width: 58px;
    white-space: nowrap;
}

.ev-icono-aviso {
    position: relative;
    display: inline-flex;
    align-items: center;
    margin-left: 2px;
    cursor: pointer;
}

.ev-icono-bloqueante {
    color: #c62828;
}

.ev-icono-revisar {
    color: #d48806;
}

.ev-icono-num {
    position: absolute;
    top: -7px;
    right: -9px;
    min-width: 15px;
    height: 15px;
    line-height: 15px;
    border-radius: 8px;
    color: #fff;
    background: #c62828;
    font-size: 0.6rem;
    font-weight: 700;
    text-align: center;
}

.ev-icono-revisar .ev-icono-num {
    background: #d48806;
}

/* Notas informativas: sin colorear la fila, solo el icono */
.ev-icono-info {
    color: #4a7dbf;
}

.ev-icono-info .ev-icono-num {
    background: #4a7dbf;
}

/* Resaltado temporal tras saltar a un documento */
.grupo-venta-resaltada td {
    animation: ev-resaltar 2.5s ease-out;
}

@keyframes ev-resaltar {
    0%,
    40% {
        background-color: #dbe7ff;
    }

    100% {
        background-color: inherit;
    }
}

.doc-enlace {
    cursor: pointer;
    text-decoration: underline;
    text-decoration-style: dotted;
    color: inherit;
}

.doc-enlace:hover {
    color: #101255;
    text-decoration-style: solid;
}

.doc-rectificada {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    font-size: 0.74rem;
    color: #5b6183;
}

.chip-exportado {
    background: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #cfe8d1;
    border-radius: 4px;
    padding: 0 5px;
    font-size: 0.66rem;
    font-weight: 700;
    cursor: help;
}

/* === Carga === */
.ev-cargando {
    padding: 28px;
    text-align: center;
    color: #5b6183;
}

.ev-cargando i {
    margin-right: 8px;
}

/* === Resumen de avisos (mismo patron que cz-resumen-avisos de Cierre Z) === */
.ev-resumen-avisos {
    margin-bottom: 16px;
    border: 1px solid #d6e4f5;
    border-left: 4px solid #4a7dbf;
    border-radius: 8px;
    background: #f3f7fc;
    color: #2f4a6d;
    font-size: 0.88rem;
}

.ev-resumen-avisos-alerta {
    border-color: #f5dfb6;
    border-left-color: #e0a12a;
    background: #fff9ee;
    color: #7a5a12;
}

.ev-resumen-avisos-bloqueante {
    border-color: #f3c9c7;
    border-left-color: #d9534f;
    background: #fff5f5;
    color: #8e2420;
}

.ev-resumen-avisos-cabecera {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    cursor: pointer;
    user-select: none;
}

.ev-resumen-avisos-texto {
    flex: 1;
}

.ev-resumen-avisos-accion {
    font-size: 0.78rem;
    font-weight: 600;
    white-space: nowrap;
    opacity: 0.85;
}

.ev-resumen-avisos-accion i {
    margin-left: 4px;
}

.ev-resumen-avisos-cuerpo {
    padding: 0 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.ev-ayuda {
    opacity: 0.8;
}

.ev-aviso-grupo {
    border-radius: 6px;
    padding: 8px 12px;
    background: #fff;
    border: 1px solid #e4e7ee;
    border-left: 4px solid #b0b4c6;
    color: #3c4166;
    cursor: pointer;
    transition: box-shadow 0.15s ease;
}

.ev-aviso-grupo:hover {
    box-shadow: 0 2px 8px rgba(16, 18, 85, 0.08);
}

.ev-aviso-grupo-activo {
    box-shadow: 0 0 0 2px #101255 !important;
}

.ev-aviso-grupo-titulo {
    font-weight: 700;
    font-size: 0.84rem;
}

.ev-aviso-grupo-titulo > i:first-child {
    margin-right: 6px;
}

.ev-aviso-filtrado {
    margin-left: 8px;
    color: #101255;
}

.ev-aviso-grupo ul {
    margin: 6px 0 0;
    padding-left: 22px;
    font-size: 0.82rem;
    color: #5b6183;
}

.ev-aviso-bloqueante {
    border-left-color: #d9534f;
    background: #fff5f5;
}

.ev-aviso-bloqueante .ev-aviso-grupo-titulo {
    color: #b3261e;
}

.ev-aviso-revisar {
    border-left-color: #e0a12a;
    background: #fffaf0;
}

.ev-aviso-revisar .ev-aviso-grupo-titulo {
    color: #8d6e00;
}

.ev-aviso-info {
    border-left-color: #4a7dbf;
    background: #f3f7fc;
}

.ev-aviso-info .ev-aviso-grupo-titulo {
    color: #2f4a6d;
}

.ev-contador {
    display: inline-block;
    min-width: 20px;
    text-align: center;
    border-radius: 10px;
    padding: 0 6px;
    margin-left: 6px;
    background: rgba(0, 0, 0, 0.07);
    font-size: 0.74rem;
}

.ev-nivel-etiqueta {
    display: inline-block;
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 4px;
    font-size: 0.64rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background: rgba(0, 0, 0, 0.06);
}

/* === Barra de filtros de la tabla === */
/* Separada de los resumenes: con el margen justo quedaba todo pegado */
.ev-barra {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin: 26px 0 14px;
    /* Hueco para la barra superior fija al hacer scroll hasta aqui */
    scroll-margin-top: 80px;
}

.ev-barra-derecha {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-left: auto;
}

/* En ancho, "Quitar filtros" a la izquierda de los desplegables */
.ev-barra-derecha .ev-btn-quitar {
    order: -1;
}

/* En estrecho la barra se parte en lineas: el boton pasa a la derecha */
@media (max-width: 1200px) {
    .ev-barra-derecha .ev-btn-quitar {
        order: 1;
    }
}

/* Mismo lenguaje que los chips: pastilla con borde, sin margenes de .btn */
.ev-btn-quitar {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    border: 1.5px dashed #6b7194;
    background: #f6f7fb;
    color: #3c4166;
    font-weight: 600;
    border-radius: 16px;
    padding: 4px 12px;
    font-size: 0.8rem;
    line-height: 1.5;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.ev-btn-quitar:hover {
    background: #fdecea;
    border-color: #e6a19c;
    color: #b3261e;
}

.ev-btn-quitar i {
    font-size: 0.72rem;
}

.ev-buscador {
    position: relative;
    flex: 1 1 240px;
    max-width: 340px;
}

.ev-buscador > i {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #9aa0b8;
    font-size: 0.8rem;
}

.ev-buscador .form-control {
    padding-left: 30px;
    padding-right: 28px;
}

.ev-limpiar {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    background: none;
    color: #9aa0b8;
    cursor: pointer;
}

.ev-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.ev-chip {
    border: 1px solid #d8dbe6;
    background: #fff;
    color: #3c4166;
    border-radius: 16px;
    padding: 4px 12px;
    font-size: 0.8rem;
    cursor: pointer;
}

.ev-chip-num {
    display: inline-block;
    margin-left: 4px;
    min-width: 18px;
    border-radius: 9px;
    padding: 0 5px;
    background: #eef0f7;
    font-size: 0.72rem;
}

.ev-chip-conAvisos .ev-chip-num {
    background: #fff1d6;
    color: #8d6e00;
}

.ev-chip-bloqueantes .ev-chip-num {
    background: #fdecea;
    color: #b3261e;
}

.ev-chip-listas .ev-chip-num {
    background: #eef7f0;
    color: #1d6f42;
}

.ev-chip-activo {
    background: #101255;
    border-color: #101255;
    color: #fff;
}

.ev-chip-activo .ev-chip-num {
    background: rgba(255, 255, 255, 0.2) !important;
    color: #fff !important;
}

.ev-select {
    width: auto;
    min-width: 150px;
}

/* "Todos los documentos" no cabia en 150px */
.ev-select-tipo-doc {
    min-width: 190px;
}

.resumen-titulo-con-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.ev-select-avanzadas {
    min-width: 0;
    width: auto;
    height: 28px;
    padding: 2px 24px 2px 8px;
    font-size: 12px;
    font-weight: normal;
    text-transform: none;
    letter-spacing: 0;
}

.ev-filtros-activos {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 10px;
}

.ev-filtro-activo {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #eef2ff;
    color: #3b4a9c;
    border: 1px solid #d8ddf7;
    border-radius: 16px;
    padding: 3px 10px;
    font-size: 0.78rem;
}

.ev-filtro-activo i {
    cursor: pointer;
}

/* Filas de los resumenes que filtran la tabla */
.resumen-fila-filtro {
    cursor: pointer;
}

.resumen-fila-filtro:hover td {
    background: #f6f7fb;
}

.resumen-fila-activa td {
    background: #eef2ff;
    font-weight: 600;
}

/* === Paginacion === */
/* Tres columnas: hueco | paginas centradas | rango y por pagina */
.ev-paginacion {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 14px;
    margin: 8px 0 90px;
    font-size: 0.84rem;
}

/* Pegado a la derecha de las paginas, no al borde: las paginas siguen centradas */
.ev-paginacion-info {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    white-space: nowrap;
}

.ev-select-pagina {
    min-width: 0;
}

/* En pantallas estrechas, todo apilado y centrado */
@media (max-width: 900px) {
    .ev-paginacion {
        grid-template-columns: 1fr;
        justify-items: center;
    }

    .ev-paginacion-info {
        justify-content: center;
    }
}

/* Botones de pagina: mismos colores que la paginacion de Productos BC */
.ev-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    list-style: none;
    padding: 0;
    margin: 0;
}

.ev-pagination .page-item {
    margin: 2px;
}

/* Como en Productos BC: numero gris oscuro sobre blanco, casilla cuadrada
   con esquinas redondeadas y la activa en turquesa */
.ev-pagination .page-item {
    margin: 0 3px;
}

.ev-pagination .page-link {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    height: 40px;
    padding: 0 10px;
    font-size: 1rem;
    line-height: 1;
    text-decoration: none;
    color: #66615b;
    background-color: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    transition: all 0.2s ease;
    cursor: pointer;
}

.ev-pagination .page-link:hover {
    color: #fff;
    background-color: #1abbb8;
    border-color: #1abbb8;
}

.ev-pagination .page-item.active .page-link {
    color: #fff;
    font-weight: 700;
    background-color: #46c0bd;
    border-color: #46c0bd;
}

.ev-pagination .page-item.disabled .page-link,
.ev-pagination .page-item.disabled .page-link:hover {
    color: #c4c7d4;
    background-color: #fff;
    border-color: #e9ecef;
    cursor: not-allowed;
    pointer-events: none;
}

.btn-desplegar {
    background: none;
    border: none;
    color: #5b6183;
    padding: 2px 4px;
    cursor: pointer;
}

.btn-desplegar:hover {
    color: #101255;
}

.doc-linea {
    display: flex;
    align-items: center;
    gap: 8px;
}

/* Ancho fijo: "Ticket", "Factura" y "Rectificativa" miden distinto, y sin esto
   el número de documento empezaba en un sitio por fila. */
.badge-doc {
    flex: 0 0 96px;
    text-align: center;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.doc-rectifica {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    font-size: 0.74rem;
    color: #c62828;
}

.doc-rectifica .doc-numero {
    font-weight: 600;
}

.chip-origen {
    background: #f3e5f5;
    color: #7b1fa2;
    border: 1px solid #e8d3ec;
    border-radius: 4px;
    padding: 0 5px;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    cursor: help;
}

.badge-doc-T {
    background: #eef0f7;
    color: #5b6183;
}

.badge-doc-F {
    background: #e3f2fd;
    color: #1565c0;
}

.badge-doc-R {
    background: #fdecea;
    color: #c62828;
}

.doc-numero {
    font-weight: 600;
}

.badge-socio {
    display: inline-block;
    margin-left: 6px;
    background: #e8f5e9;
    color: #2e7d32;
    border-radius: 4px;
    padding: 0 5px;
    font-size: 0.7rem;
}

.texto-varias {
    font-weight: 600;
    color: #5b6183;
}

.acciones-btns {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
}

/* El lápiz es un enlace y el candado un span: sin una caja común quedaban a
   distinta altura. */
.accion-icono {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    line-height: 1;
    cursor: pointer;
}

.accion-icono i {
    font-size: 1rem;
    line-height: 1;
}

.accion-info i {
    color: #007bff;
}

.accion-editar i {
    color: orange;
}

.accion-bloqueada {
    cursor: default;
}

.accion-bloqueada i {
    color: #b0b4c6;
}

/* Fila desplegable con el desglose, los vales y los avisos */
/* Venta desplegada: la fila y su detalle comparten un tinte (el de su nivel
   de aviso si lo tiene) y el detalle va en una tarjeta blanca, para que se
   vea claramente que pertenece a esa fila */
.grupo-venta-abierta > tr > td {
    background-color: #eef1fa;
}

.grupo-venta-abierta.grupo-nivel-bloqueante > tr > td {
    background-color: #fdeeee;
}

.grupo-venta-abierta.grupo-nivel-revisar > tr > td {
    background-color: #fff8e8;
}

.grupo-venta-abierta.grupo-nivel-info > tr > td {
    background-color: #eef4fc;
}

/* Sin linea entre la fila y su detalle: son un mismo bloque */
.grupo-venta-abierta > tr:first-child > td {
    border-bottom-color: transparent;
}

.grupo-venta-abierta .btn-desplegar {
    color: #101255;
}

.fila-detalle td {
    background-color: #fbfbfe;
    padding-top: 0 !important;
    padding-bottom: 14px !important;
}

.detalle-panel {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 28px;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #dfe3ef;
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(16, 18, 85, 0.06);
}

.detalle-bloque {
    min-width: 0;
}

/* Los avisos ocupan su propia linea, encima del desglose y los vales */
.detalle-avisos {
    flex-basis: 100%;
}

/* Nombre, código e importe viajan juntos dentro de la misma ficha, en lugar de
   quedar en extremos opuestos de una tabla al ancho de la fila. */
.fichas {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.ficha {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border: 1px solid #e4e7ee;
    border-radius: 8px;
    padding: 5px 10px;
    font-size: 0.84rem;
}

.ficha-nombre {
    color: #3c4166;
}

.ficha-importe {
    font-weight: 700;
    color: #101255;
}

.detalle-bloque h6 {
    margin: 0 0 8px 0;
    font-size: 0.74rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #101255;
}

.lista-avisos {
    list-style: none;
    margin: 0;
    padding: 0;
}

.lista-avisos li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 3px 0;
    font-size: 0.84rem;
    color: #c62828;
}

.lista-avisos li i {
    margin-top: 2px;
}

.lista-avisos li.aviso-revisar {
    color: #8d6e00;
}

.lista-avisos li.aviso-info {
    color: #2f4a6d;
}

.lista-avisos li .ev-nivel-etiqueta {
    margin: 0 6px 0 0;
}

/* Cada medio de pago del detalle en su línea */
.detalle-pago {
    display: block;
}

.detalle-pago strong {
    margin-left: 6px;
}

/* === Modal de edición === */
.resumen-venta-editada {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 10px;
    background: #f6f7fb;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 18px;
}

.resumen-venta-editada span {
    display: block;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #7a82a8;
}

.opcion-codigo {
    float: right;
    color: #8a90ab;
    font-size: 0.78rem;
}

.obligatorio {
    color: #c62828;
}

.aviso-cliente-rojo {
    color: #c62828;
}

/* === Modal de detalle de la venta: ficha compacta === */
.dv-body {
    padding: 16px 22px 20px;
    max-height: 75vh;
    overflow-y: auto;
}

.dv-cabecera {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 4px 0 18px;
    margin-bottom: 6px;
    border-bottom: 1px solid #eef0f5;
}

.dv-doc {
    font-weight: 700;
    color: #101255;
}

.dv-rectifica {
    margin-top: 2px;
    color: #c62828;
}

.dv-importe {
    font-size: 1.4rem;
    font-weight: 700;
    color: #101255;
    text-align: right;
    white-space: nowrap;
}

.dv-importe small {
    font-size: 0.72rem;
    font-weight: 400;
    color: #7a82a8;
}

.dv-columnas {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 0 14px;
}

/* Cada seccion es una tarjeta con su color: fondo suave y titulo con icono,
   mas grande que el texto para que se distingan de un vistazo */
.dv-seccion {
    --dv-color: #101255;
    --dv-fondo: #f6f7fb;
    margin-top: 14px;
    padding: 12px 14px;
    background: var(--dv-fondo);
    border: 1px solid #e4e7ee;
    border-radius: 10px;
}

.dv-seccion-cliente {
    --dv-color: #4a7dbf;
    --dv-fondo: #f3f7fc;
}

.dv-seccion-pago {
    --dv-color: #2a9d99;
    --dv-fondo: #f0faf9;
}

.dv-seccion h6 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 14px;
    font-size: 0.95rem;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0;
    color: var(--dv-color);
}

.dv-seccion h6 i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    background: var(--dv-color);
    color: #fff;
    font-size: 0.72rem;
}

.dv-seccion h6 small {
    font-weight: 400;
}

/* Etiqueta y valor en la misma fila, con el mismo peso de letra */
.dv-datos {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 4px 14px;
    margin: 0;
    font-size: 0.86rem;
}

.dv-datos dt {
    font-weight: 400;
    color: #4a5070;
}

.dv-datos dd {
    margin: 0;
    color: #1e2535;
    word-break: break-word;
}

.dv-vacio {
    margin: 0;
    font-size: 0.86rem;
}

/* Lineas: tarjeta blanca y la tabla con cabecera tintada y filas alternas */
.dv-lineas {
    background: #fff;
}

.dv-tabla-lineas thead th {
    background: #eef0f7;
}

.dv-tabla-lineas thead th:first-child {
    border-top-left-radius: 6px;
}

.dv-tabla-lineas thead th:last-child {
    border-top-right-radius: 6px;
}

.dv-tabla-lineas tbody tr:nth-child(even) td {
    background: #fafbfd;
}

.dv-tabla-lineas tbody tr:hover td {
    background: #f1f4fb;
}

.dv-tabla-lineas td:last-child {
    font-weight: 600;
    color: #101255;
}

.dv-cargando {
    padding: 14px;
    text-align: center;
    color: #7a82a8;
    font-size: 0.86rem;
    background: #fafbfd;
    border-radius: 8px;
}

.dv-cargando i {
    margin-right: 6px;
}

.dv-tabla-lineas {
    margin: 0;
    font-size: 0.84rem;
}

.dv-tabla-lineas th {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #3c4166;
    border-top: none;
    border-bottom: 2px solid #e4e7ee;
    padding: 6px 8px;
}

.dv-tabla-lineas td {
    padding: 7px 8px;
    vertical-align: middle;
    color: #1e2535;
    border-color: #eef0f5;
}

/* Unidades estrechas: mas sitio para el nombre del producto */
.dv-col-uds {
    width: 48px;
    white-space: nowrap;
}

.dv-col-bc {
    width: 150px;
    white-space: nowrap;
}

/* Codigo de ancho fijo y cifras tabulares: codigos alineados a la derecha
   dentro de su caja y las unidades arrancando todas en la misma vertical */
.dv-codigo-bc {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.dv-codigo {
    display: inline-block;
    min-width: 58px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-size: 0.78rem;
}

.dv-unidad {
    font-size: 0.76rem;
    font-weight: 600;
    color: #4a5070;
}

.dv-col-unidad {
    width: 80px;
    white-space: nowrap;
}

/* Menus: la fila del menu destaca y sus platos van sangrados debajo */
.dv-tabla-lineas .dv-linea-menu td {
    background: #f6f7fb;
    font-weight: 600;
}

.dv-tabla-lineas .dv-linea-plato .dv-col-producto {
    padding-left: 26px;
}

.dv-tabla-lineas .dv-linea-plato td {
    border-top-style: dashed;
}

.dv-plato-marca {
    margin-right: 6px;
    font-size: 0.72rem;
    color: #8a90ad;
}

.dv-chip-menu {
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 4px;
    background: #eef2ff;
    border: 1px solid #d8ddf7;
    color: #3b4a9c;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    vertical-align: middle;
}

.dv-incluido {
    font-size: 0.76rem;
    font-style: italic;
    color: #5b6183;
}

/* Textos secundarios del modal: el gris de text-muted se perdia */
.dv-body .text-muted,
.dv-texto-suave {
    color: #5b6183 !important;
}

/* Por encima de las filas alternas y del hover */
.dv-tabla-lineas tbody tr.dv-linea-aviso td {
    background: #fff5f5;
}

.dv-aviso-linea {
    display: block;
    font-size: 0.74rem;
    color: #c62828;
}

/* Total de las lineas, cerrando la tabla */
.dv-tabla-lineas tfoot td {
    padding: 8px;
    border-top: 2px solid #e4e7ee;
    background: #eef0f7;
    font-weight: 700;
    color: #101255;
}

.dv-tabla-lineas tfoot tr.dv-total-venta td {
    border-top: none;
    background: #f6f7fb;
    font-weight: 600;
    color: #3c4166;
}

.dv-tabla-lineas tfoot tr:last-child td:first-child {
    border-bottom-left-radius: 6px;
}

.dv-tabla-lineas tfoot tr:last-child td:last-child {
    border-bottom-right-radius: 6px;
}

/* Grupo solo informativo: no filtra al pulsarlo */
.ev-aviso-grupo-fijo {
    cursor: default;
}

.ev-aviso-grupo-fijo:hover {
    box-shadow: none;
}

/* === Modal previo a la exportacion === */
.dv-seccion-aviso {
    --dv-color: #b26a00;
    --dv-fondo: #fff9ee;
}

.ex-texto {
    margin: 0 0 8px;
    font-size: 0.88rem;
    color: #3c4166;
}

.ex-texto-suave {
    margin-bottom: 0;
    font-size: 0.82rem;
    color: #5b6183;
}

.ex-pregunta {
    margin: 18px 0 0;
    text-align: center;
    font-size: 1.05rem;
    font-weight: 700;
    color: #101255;
}

/* Botones del pie: pastillas como el resto de la vista */
.ev-btn-modal {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 120px;
    justify-content: center;
    margin: 0;
    padding: 8px 18px;
    border: 1px solid #d8dbe6;
    border-radius: 20px;
    background: #fff;
    color: #3c4166;
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.ev-btn-modal:hover:not(:disabled) {
    background: #f6f7fb;
    border-color: #b8bdd3;
}

.ev-btn-modal-primario {
    background: #101255;
    border-color: #101255;
    color: #fff;
}

.ev-btn-modal-primario:hover:not(:disabled) {
    background: #0a0f3c;
    border-color: #0a0f3c;
}

.ev-btn-modal:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
</style>


// WEBPACK FOOTER //
// src/pages/Dashboard/Integraciones/BusinessCentral/ExportacionVentas/ExportacionVentasBusinessCentral.vue